import React, { useEffect, useState } from "react";
import {
  Building2,
  FileText,
  Network,
  User,
  Database,
  Bot,
  Zap,
  ChevronRight,
  ArrowLeft,
  Trash2,
  X,
  PlugZap,
  CheckCircle2,
  Plus,
  AlertCircle,
  Cpu,
  Layers,
  MessageSquare,
  Music,
  FileAudio,
  FileVideo,
  Mic,
  Video,
  Edit2,
  Play,
  Check,
  Loader
} from "lucide-react";
import { StepStepper } from "../../index";
import {
  companyService,
  dataSeedingService,
  fileConfigurationService,
  sqlConfigurationService,
  workspaceService,
  modelConfigurationService,
  mediaService,
  mlService,
} from "../../../../services/index.js";
import ConfirmDeleteModal from "./ConfirmDeleteModel.jsx";

const STEPS = [
  { step: 1, label: "Company Profile", desc: "Basic business details" },
  { step: 2, label: "Model Config", desc: "LLMs & Agents setup" },
  { step: 3, label: "SQL Analytics", desc: "Database connections" },
  { step: 4, label: "File Analytics", desc: "Document indexing" },
  { step: 5, label: "Audio/Video Analytics", desc: "Multimedia processing" },
  { step: 6, label: "ML Setup", desc: "Datasets & prediction models" },
  { step: 7, label: "Review & Finish", desc: "Verify configuration" },
];
const SIDEBAR_AUTO_COLLAPSE_BREAKPOINT = 1200;

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-[#1d57a5] outline-none transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none";
const labelCls =
  "block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5";
const sectionHeadCls =
  "text-sm font-bold text-slate-800 dark:text-slate-200 mb-5 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2";

const pickVal = (obj = {}, keys = []) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
    const fallback = Object.keys(obj || {}).find(
      (k) => k.toLowerCase() === String(key).toLowerCase(),
    );
    if (fallback && obj[fallback] !== undefined && obj[fallback] !== null) {
      return obj[fallback];
    }
  }
  return "";
};

const getCompanyIdFromPayload = (payload = {}) =>
  Number(
    pickVal(payload, ["companyID", "companyId", "id", "companyMasterId"]) || 0,
  );

const getCompanyIdFromCreateResponse = (payload = {}) =>
  Number(
    getCompanyIdFromPayload(payload) ||
      getCompanyIdFromPayload(payload?.data) ||
      getCompanyIdFromPayload(payload?.result) ||
      0,
  );

const readArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.result)) return value.result;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const parseCsv = (value = "") =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeCsvHeaders = (text = "") => {
  const firstLine = String(text || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .find((line) => String(line || "").trim() !== "");
  if (!firstLine) return [];
  return firstLine
    .split(",")
    .map((item) => item.trim().replace(/^"(.*)"$/, "$1"))
    .filter(Boolean);
};

const getDisplayName = (item = {}, keys = []) =>
  String(pickVal(item, keys) || "").trim();

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const toServiceType = (value = "") => {
  const normalized = String(value).toLowerCase();
  if (normalized.includes("sql")) return "SqlAnalytics";
  if (normalized.includes("file")) return "FileAnalytics";
  return "";
};

const findValueByKeyFragment = (node, fragments = [], depth = 0) => {
  if (!node || depth > 5) return "";
  const frags = fragments.map((f) => String(f).toLowerCase());

  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findValueByKeyFragment(item, fragments, depth + 1);
      if (found !== "" && found !== null && found !== undefined) return found;
    }
    return "";
  }

  if (typeof node !== "object") return "";

  for (const [key, value] of Object.entries(node)) {
    const keyLower = String(key).toLowerCase();
    const matches = frags.every((frag) => keyLower.includes(frag));
    if (
      matches &&
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  for (const value of Object.values(node)) {
    const found = findValueByKeyFragment(value, fragments, depth + 1);
    if (found !== "" && found !== null && found !== undefined) return found;
  }

  return "";
};

const buildCompanyFormData = (details = null) => ({
  companyName: pickVal(details, ["companyName"]) || "",
  businessTradeName: pickVal(details, ["businessTradeName"]) || "",
  industryType: pickVal(details, ["industryType", "industry"]) || "",
  companyRegistrationNumber:
    pickVal(details, ["companyRegistrationNumber", "registrationNumber"]) || "",
  dateOfIncorporation: pickVal(details, ["dateOfIncorporation"]) || "",
  companySize: pickVal(details, ["companySize"]) || "",
  description: pickVal(details, ["description"]) || "",
  registeredAddress: pickVal(details, ["registeredAddress"]) || "",
  corporateAddress: pickVal(details, ["corporateAddress"]) || "",
  primaryPhone: pickVal(details, ["primaryPhone", "phone"]) || "",
  officialEmail: pickVal(details, ["officialEmail", "email"]) || "",
  companyWebsite: pickVal(details, ["companyWebsite", "website"]) || "",
  contactPersonName:
    pickVal(details, ["contactPersonName", "contactPerson"]) || "",
  designationRole: pickVal(details, ["designationRole", "designation"]) || "",
  workEmail: pickVal(details, ["workEmail"]) || "",
  mobileNumber: pickVal(details, ["mobileNumber", "mobileNo"]) || "",
  alternateContact: pickVal(details, ["alternateContact"]) || "",
  gstNo: pickVal(details, ["gstNo", "gstNO", "gst"]) || "",
  panNo: pickVal(details, ["panNo", "panNO", "pan"]) || "",
  tanNo: pickVal(details, ["tanNo", "tanNO", "tan"]) || "",
  createdBy: 0,
});

const AGENT_TYPES = [
  { id: "orchestrator", name: "Orchestrator" },
  { id: "final_response", name: "Final Response" },
  { id: "sql", name: "SQL" },
  { id: "file", name: "File" },
  { id: "embedding", name: "Embedding" },
];


export default function CompanyOnboarding({
  step,
  setStep,
  onFinish,
  selectedCompanyDetails,
  viewOnly = false,
  onEnableEdit,
}) {
  // ==================== STEP 1: COMPANY PROFILE STATES ====================
  const [formData, setFormData] = useState(() =>
    buildCompanyFormData(selectedCompanyDetails),
  );
  const [CompanyID, SetCompanyID] = useState(() =>
    getCompanyIdFromPayload(selectedCompanyDetails),
  );
  const [companyName, setCompanyName] = useState(formData.companyName);
  const [seedStartError, setSeedStartError] = useState("");
  const [isStartingMediaIngestion, setIsStartingMediaIngestion] = useState(false);
  const [isStartingDBIngestion, setIsStartingDBIngestion] = useState(false);
  const [isStartingFileIngestion, setIsStartingFileIngestion] = useState(false);
  const [isStartingIngestion, setIsStartingIngestion] = useState(false);
  const [manualSidebarCollapsed, setManualSidebarCollapsed] = useState(null);
  const [isAutoSidebarCollapsed, setIsAutoSidebarCollapsed] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableAgents, setAvailableAgents] = useState([]);

  // ==================== MODEL CONFIGURATION STATES ====================
  const [modelGroups, setModelGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [isFirstModel, setIsFirstModel] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [modelErrors, setModelErrors] = useState({});
  const [agentConfigs, setAgentConfigs] = useState({});
  const agentConfigsRef = React.useRef({});
  const setAgentConfigsWithRef = (updater) => {
    setAgentConfigs((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      agentConfigsRef.current = next;
      return next;
    });
  };
  const [isSavingModel, setIsSavingModel] = useState(false);
  const [modelSaved, setModelSaved] = useState(false);
  const [activeAgentIdx, setActiveAgentIdx] = useState(0);
  const [maxReachedAgentIdx, setMaxReachedAgentIdx] = useState(0);
  const [toast, setToast] = useState(null);
  const isSidebarCollapsed =
    typeof manualSidebarCollapsed === "boolean"
      ? manualSidebarCollapsed
      : isAutoSidebarCollapsed;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ==================== STEP 3: SQL ANALYTICS STATES ====================
  const [dbTypes, setDbTypes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [testConnectionPayload, setTestConnectionPayload] = useState({
    databaseType: "",
    serverName: "",
    dbUserName: "",
    dbPassword: "",
    portNum: "",
  });
  const [dbData, setDbData] = useState([]);
  const [connection, setConnection] = useState("");
  const [schemaList, setSchemaList] = useState([]);
  const [DBInfo, setDBInfo] = useState({});
  const [tableList, setTableList] = useState([]);
  const [viewList, setViewList] = useState([]);
  const [selectedTables, setSelectedTables] = useState("");
  const [selectedViews, setSelectedViews] = useState("");
  const [selectedDB, setSelectedDB] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [description, setDescription] = useState("");
  const [promptConfiguration, setPromptConfiguration] = useState("");
  const [selectedTablesArray, setSelectedTablesArray] = useState([]);
  const [selectedViewsArray, setSelectedViewsArray] = useState([]);
  const [existingUploadedFiles, setExistingUploadedFiles] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [connectionSuccess, setConnectionSuccess] = useState(false);

  // ==================== STEP 4: FILE ANALYTICS STATES ====================
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filePromptConfiguration, setFilePromptConfiguration] = useState("");
  const [cloudSource, setCloudSource] = useState("");
  const [cloudConfig, setCloudConfig] = useState({
    bucket: "",
    region: "",
    accessKey: "",
    secretKey: "",
    containerName: "",
    connectionString: "",
    host: "",
    port: "",
    username: "",
    password: "",
    remotePath: "",
    folderName: "",
  });
  const [cloudConnectionStatus, setCloudConnectionStatus] = useState("idle");
  const [cloudFiles, setCloudFiles] = useState([]);
  const [selectedCloudFiles, setSelectedCloudFiles] = useState([]);
  const [cloudConnectionErrorMessage, setCloudConnectionErrorMessage] =
    useState("");

  // ==================== STEP 5: AUDIO/VIDEO ANALYTICS STATES ====================
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [deleteMediaConfirm, setDeleteMediaConfirm] = useState(null);
  const [uploadMediaStatus, setUploadMediaStatus] = useState(false);

  // ==================== STEP 6: ML SETUP STATES ====================
  const [mlModelTypes, setMlModelTypes] = useState([]);
  const [mlDataSets, setMlDataSets] = useState([]);
  const [mlExistingModels, setMlExistingModels] = useState([]);
  const [mlColumns, setMlColumns] = useState([]);
  const [selectedDataSetId, setSelectedDataSetId] = useState("");
  const [selectedDataSetPath, setSelectedDataSetPath] = useState("");
  const [isUploadingMlDataset, setIsUploadingMlDataset] = useState(false);
  const [isSavingMlModels, setIsSavingMlModels] = useState(false);
  const [isDeletingMlModel, setIsDeletingMlModel] = useState(false);
  const [buildingMlModelIds, setBuildingMlModelIds] = useState([]);
  const [editingMlModelId, setEditingMlModelId] = useState(null);
  const [mlModelCards, setMlModelCards] = useState([]);
  // ==================== HELPER FUNCTIONS ====================
  const validate = (data) => {
    const errs = {};
    if (!data.companyName?.trim())
      errs.companyName = "Company name is required.";
    if (!data.businessTradeName?.trim())
      errs.businessTradeName = "Business/Trade name is required.";
    if (!data.industryType?.trim())
      errs.industryType = "Industry type is required.";
    if (!data.companyRegistrationNumber?.trim())
      errs.companyRegistrationNumber = "Registration number is required.";
    if (!data.officialEmail?.trim()) {
      errs.officialEmail = "Official email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.officialEmail)) {
      errs.officialEmail = "Enter a valid email address.";
    }
    if (!data.gstNo?.trim()) {
      errs.gstNo = "GST number is required.";
    } else if (
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        data.gstNo.toUpperCase(),
      )
    ) {
      errs.gstNo = "Enter a valid GST number (e.g. 22AAAAA0000A1Z5).";
    }
    if (!data.panNo?.trim()) {
      errs.panNo = "PAN number is required.";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(data.panNo.toUpperCase())) {
      errs.panNo = "Enter a valid PAN number (e.g. ABCDE1234F).";
    }
    if (!data.tanNo?.trim()) {
      errs.tanNo = "TAN number is required.";
    } else if (!/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(data.tanNo.toUpperCase())) {
      errs.tanNo = "Enter a valid TAN number (e.g. ABCD12345E).";
    }
    return errs;
  };

  const getActiveGroup = () => modelGroups.find((g) => g.id === activeGroupId);
  const getResolvedCompanyId = () =>
    Number(
      CompanyID ||
        getCompanyIdFromPayload(selectedCompanyDetails) ||
        localStorage.getItem("onboardingCompanyId") ||
        0,
    );

  // ==================== MODEL CONFIGURATION FUNCTIONS ====================

  const getAgentConfigKey = (agentType) =>
    `${activeGroupId}_agent_${agentType}`;

  const getAgentConfig = (agentType) =>
    agentConfigs[getAgentConfigKey(agentType)] || {
      apiEndpoint: "",
      apiKey: "",
      modelId: "",
      temperature: 0.7,
      maxTokens: 2000,
      systemPrompt: "",
      useDefaultPrompt: false,
    };

  const setAgentConfigField = (agentType, field, value) => {
    setModelSaved(false);
    if (field === "apiKey" || field === "modelId") {
      setModelErrors((prev) => {
        const updated = { ...prev };
        delete updated[`${field}_${agentType}`];
        return updated;
      });
    }
    setAgentConfigsWithRef((prev) => ({
      ...prev,
      [getAgentConfigKey(agentType)]: {
        ...getAgentConfig(agentType),
        [field]: value,
      },
    }));
  };

const defaultAgentConfig = {
  apiEndpoint: "",
  apiKey: "",
  modelId: "",
  temperature: 0.7,
  maxTokens: 2000,
  systemPrompt: "",
  useDefaultPrompt: false,
};

const handleAddGroup = () => {
  if (!newGroupName.trim()) return;
  setModelSaved(false);

  const newGroup = {
    id: `group_${Date.now()}`,
    name: newGroupName,
  };

  setModelGroups((prev) => [...prev, newGroup]);
  setActiveGroupId(newGroup.id);
  setNewGroupName("");
  setShowAddGroup(false);

  setAgentConfigsWithRef((prev) => {
    const updated = { ...prev };

    availableAgents.forEach((agent) => {
      const newKey = `${newGroup.id}_agent_${agent.agentType}`;

      if (isFirstModel) {
        // Copy from the "null" group keys that were filled before a group existed
        const nullKey = `null_agent_${agent.agentType}`;
        const fallbackKey = `undefined_agent_${agent.agentType}`;
        const sourceConfig =
          prev[nullKey] || prev[fallbackKey] || defaultAgentConfig;

        updated[newKey] = {
          configId: 0,
          apiEndpoint: sourceConfig.apiEndpoint || "",
          apiKey: sourceConfig.apiKey || "",
          modelId: sourceConfig.modelId || "",
          temperature: sourceConfig.temperature ?? 0.7,
          maxTokens: sourceConfig.maxTokens ?? 2000,
          systemPrompt: sourceConfig.systemPrompt || "",
          useDefaultPrompt: sourceConfig.useDefaultPrompt || false,
        };
      } else {
        updated[newKey] = { ...defaultAgentConfig, configId: 0 };
        setActiveAgentIdx(0);
      }
    });

    return updated;
  });

  setIsFirstModel(false);
};

  const handleSelectGroup = async (groupId) => {
    setActiveGroupId(groupId);
    setActiveAgentIdx(0);
    setMaxReachedAgentIdx(0);
    setModelSaved(false);
    if (String(groupId).startsWith("group_")) return;
    const group = modelGroups.find((g) => g.id === groupId);
    const numericGroupId = group?.modelGroupId || groupId;
    try {
      const res =
        await modelConfigurationService.getAgentConfigsByModelGroup(
          numericGroupId,
        );
      const agentList = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      if (agentList.length > 0) {
        const responseModelGroupId = agentList[0]?.modelGroupId;
        if (responseModelGroupId) {
          setModelGroups((prev) =>
            prev.map((g) =>
              g.id === groupId
                ? { ...g, modelGroupId: responseModelGroupId }
                : g,
            ),
          );
        }
        setAgentConfigsWithRef((prev) => {
          const updated = { ...prev };
          agentList.forEach((ac) => {
            const key = `${groupId}_agent_${ac.agentType}`;
            updated[key] = {
              configId: ac.configId || ac.agentConfigId || 0,
              modelGroupId: ac.modelGroupId,
              apiEndpoint: ac.apiEndpoint || "",
              apiKey: ac.apiKey || "",
              modelId: ac.modelId || "",
              temperature: ac.temperature ?? 0.7,
              maxTokens: ac.maxTokens ?? 2000,
              systemPrompt: ac.systemPrompt || "",
              useDefaultPrompt: !ac.systemPrompt,
            };
          });
          return updated;
        });
      }
    } catch (e) {
      console.error("Failed to load agent configs for group:", e);
    }
  };

  useEffect(() => {
    if (!activeGroupId && modelGroups.length > 0) {
      handleSelectGroup(modelGroups[0].id);
    }
  }, [modelGroups, activeGroupId]);

  const handleDeleteGroup = async () => {
    if (modelGroups.length === 1) return;
    const group = modelGroups.find((g) => g.id === activeGroupId);
    if (group?.modelGroupId) {
      try {
        const deletedBy = Number(localStorage.getItem("userId") || 0);
        await modelConfigurationService.deleteModelGroup(
          group.modelGroupId,
          deletedBy,
        );
      } catch (e) {
        console.error("Delete model group error:", e);
        return;
      }
    }
    const updatedGroups = modelGroups.filter((g) => g.id !== activeGroupId);
    setModelGroups(updatedGroups);
    setActiveGroupId(updatedGroups[0]?.id);
  };

  const validateModelConfig = () => {
    const errs = {};
    availableAgents.forEach((agent) => {
      const key = `${activeGroupId}_agent_${agent.agentType}`;
      const cfg = agentConfigs[key] || {};
      if (!cfg.apiKey?.trim())
        errs[`apiKey_${agent.agentType}`] = "API Key is required.";
      if (!cfg.modelId?.trim())
        errs[`modelId_${agent.agentType}`] = "Model ID is required.";
    });
    setModelErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveModelConfig = async () => {
    if (!validateModelConfig()) return;
    setIsSavingModel(true);
    try {
      const resolvedCompanyId =
        Number(CompanyID) ||
        getCompanyIdFromPayload(selectedCompanyDetails) ||
        Number(localStorage.getItem("onboardingCompanyId") || 0);
      if (!resolvedCompanyId) {
        console.error("Company ID is missing. Please complete Step 1 first.");
        setIsSavingModel(false);
        return;
      }
      const userId = Number(localStorage.getItem("userId") || 0);
      for (const group of modelGroups) {
        const firstKey = `${group.id}_agent_${availableAgents[0]?.agentType}`;
        const latestConfigs = agentConfigsRef.current;
        const modelGroupId =
          latestConfigs[firstKey]?.modelGroupId || group.modelGroupId;
        const isExistingGroup = !!modelGroupId;

        const agentConfigsList = availableAgents.map((agent) => {
          const key = `${group.id}_agent_${agent.agentType}`;
          const cfg = latestConfigs[key] || {};
          return {
            agentType: agent.agentType,
            companyID: resolvedCompanyId,
            apiEndpoint: cfg.apiEndpoint || "",
            apiKey: cfg.apiKey || "",
            modelId: cfg.modelId || "",
            temperature: cfg.temperature ?? 0.7,
            maxTokens: cfg.maxTokens ?? 2000,
            systemPrompt:
              cfg.useDefaultPrompt !== false
                ? agent.defaultPrompt
                : cfg.systemPrompt || "",
            createdBy: userId,
          };
        });
        const payload = {
          modelGroup: {
            companyId: resolvedCompanyId,
            name: group.name,
            createdBy: userId,
          },
          agentConfigs: agentConfigsList,
        };

        if (isExistingGroup) {
          await modelConfigurationService.updateModelGroupWithAgents({
            modelGroup: {
              companyId: resolvedCompanyId,
              modelGroupId,
              name: group.name,
              updatedBy: userId,
            },
            agentConfigs: agentConfigsList.map((ac) => ({
              configId:
                latestConfigs[`${group.id}_agent_${ac.agentType}`]?.configId ||
                0,
              agentType: ac.agentType,
              companyID: resolvedCompanyId,
              apiEndpoint: ac.apiEndpoint,
              apiKey: ac.apiKey,
              modelId: ac.modelId,
              temperature: ac.temperature,
              maxTokens: ac.maxTokens,
              systemPrompt: ac.systemPrompt,
              updatedBy: userId,
            })),
          });
          try {
            const refreshRes =
              await modelConfigurationService.getAgentConfigsByModelGroup(
                modelGroupId,
              );
            const refreshedList = Array.isArray(refreshRes)
              ? refreshRes
              : Array.isArray(refreshRes?.data)
                ? refreshRes.data
                : [];
            if (refreshedList.length > 0) {
              setAgentConfigsWithRef((prev) => {
                const updated = { ...prev };
                refreshedList.forEach((ac) => {
                  const key = `${group.id}_agent_${ac.agentType}`;
                  updated[key] = {
                    ...updated[key],
                    configId: ac.configId || ac.agentConfigId || 0,
                    modelGroupId: ac.modelGroupId,
                  };
                });
                return updated;
              });
            }
          } catch (e) {
            console.error("Failed to refresh agent configs after update:", e);
          }
        } else {
          await modelConfigurationService.createModelGroupWithAgents(payload);
        }
      }
      setModelSaved(true);
      setActiveAgentIdx(0);
      showToast("Model configuration saved successfully.");
    } catch (error) {
      console.error("Save model config error:", error);
      showToast(
        "Failed to save model configuration. Please try again.",
        "error",
      );
    } finally {
      setIsSavingModel(false);
    }
  };
  const fetchAvailableAgents = async () => {
    try {
      const response = await modelConfigurationService.getAllAgents();
      if (response && response.data && response.data.length > 0) {
        setAvailableAgents(response.data);
      } else {
        setAvailableAgents([
          {
            agentType: 1,
            name: "Orchestrator",
            description: "Handles orchestration logic",
            supportsSystemPrompt: true,
          },
          {
            agentType: 2,
            name: "FinalResponse",
            description: "Generates final output",
            supportsSystemPrompt: true,
          },
          {
            agentType: 3,
            name: "SQL",
            description: "Handles SQL queries",
            supportsSystemPrompt: false,
          },
          {
            agentType: 4,
            name: "File",
            description: "Handles file retrieval",
            supportsSystemPrompt: false,
          },
          {
            agentType: 5,
            name: "Embedding",
            description: "Handles embeddings",
            supportsSystemPrompt: false,
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAvailableAgents([
        {
          agentType: 1,
          name: "Orchestrator",
          description: "Handles orchestration logic",
          supportsSystemPrompt: true,
        },
        {
          agentType: 2,
          name: "FinalResponse",
          description: "Generates final output",
          supportsSystemPrompt: true,
        },
        {
          agentType: 3,
          name: "SQL",
          description: "Handles SQL queries",
          supportsSystemPrompt: false,
        },
        {
          agentType: 4,
          name: "File",
          description: "Handles file retrieval",
          supportsSystemPrompt: false,
        },
        {
          agentType: 5,
          name: "Embedding",
          description: "Handles embeddings",
          supportsSystemPrompt: false,
        },
      ]);
    }
  };

  useEffect(() => {
    fetchAvailableAgents();
  }, []);
  const handleModelNext = () => setStep(3);

  // ==================== STEP 1 FUNCTIONS ====================
  useEffect(() => {
    const nextFormData = buildCompanyFormData(selectedCompanyDetails);
    setFormData(nextFormData);
    setCompanyName(nextFormData.companyName);
    SetCompanyID(getCompanyIdFromPayload(selectedCompanyDetails));
  }, [selectedCompanyDetails]);

  const handleChange = (e) => {
    if (viewOnly) return;
    if (e.target.name == "companyName") {
      setCompanyName(e.target.value);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addBasicInfo = async (data) => {
    const errs = validate(data);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      let resolvedCompanyId = getResolvedCompanyId();
      if (!selectedCompanyDetails) {
        const result = await companyService.create(data);
        resolvedCompanyId = getCompanyIdFromCreateResponse(result);
        if (!resolvedCompanyId) {
          console.error(
            "Company created but no companyId was returned:",
            result,
          );
          return;
        }
        SetCompanyID(resolvedCompanyId);
        localStorage.setItem("onboardingCompanyId", resolvedCompanyId);
      }
      if (selectedCompanyDetails) {
        await companyService.update({
          ...data,
          companyID: getCompanyIdFromPayload(selectedCompanyDetails),
          updatedBy: localStorage.getItem("userId"),
        });
      }
      setSeedStartError("");
      setStep(2);
    } catch (error) {
      console.error("Failed to create company:", error);
    }
  };

  // ==================== SQL ANALYTICS FUNCTIONS ====================
  const getSqlDbType = async () => {
    try {
      const response = await sqlConfigurationService.getSqlDbType();
      setDbTypes(response.data || []);
      setLoaded(true);
    } catch (error) {
      console.error("Get SQL database type error:", error);
    }
  };

  useEffect(() => {
    if (getResolvedCompanyId()) {
      fetchExistingMedia();
    }
  }, [selectedCompanyDetails, CompanyID]);

  useEffect(() => {
    if (!selectedCompanyDetails) {
      setExistingUploadedFiles([]);
      return;
    }

    const companyId = getCompanyIdFromPayload(selectedCompanyDetails);
    if (!companyId) return;

    let active = true;

    const hydrateConfigs = async () => {
      try {
        if (!loaded) await getSqlDbType();
        await fetchAvailableAgents();

        try {
          const modelGroupRes =
            await modelConfigurationService.getModelGroupByCompanyId(companyId);
          const groups = Array.isArray(modelGroupRes)
            ? modelGroupRes
            : Array.isArray(modelGroupRes?.data)
              ? modelGroupRes.data
              : [];
          if (groups.length > 0) {
            const hydratedGroups = groups.map((g) => ({
              id: String(g.modelGroupId || g.id || `group_${Date.now()}`),
              modelGroupId: g.modelGroupId || g.id,
              name: g.name || g.groupName || "",
              config: {},
            }));
            setModelGroups(hydratedGroups);
          }
        } catch (e) {
          console.error("Failed to load model groups:", e);
        }

        const [sqlConfigRes, sqlAndFileRes, fileConfigRes] = await Promise.all([
          sqlConfigurationService
            .getSQLConfigByCompanyId(companyId)
            .catch(() => null),
          workspaceService
            .getSQLandFileConfigByCompanyID(companyId)
            .catch(() => null),
          fileConfigurationService
            .getFileConfigByCompanyId(companyId)
            .catch(() => null),
        ]);

        if (!active) return;

        const sqlAndFileRows = readArray(sqlAndFileRes);
        const sqlConfigData = sqlConfigRes?.data || null;
        const sqlEntry =
          sqlConfigData ||
          sqlAndFileRows.find(
            (row) =>
              toServiceType(
                pickVal(row, ["serviceType", "selectedService", "service"]),
              ) === "SqlAnalytics",
          );

        if (sqlEntry) {
          const databaseType =
            pickVal(sqlEntry, ["databaseType", "dbType"]) || "";
          const connectionString =
            pickVal(sqlEntry, ["connectionString", "connectionstring"]) || "";
          const databaseName =
            pickVal(sqlEntry, ["databaseName", "database"]) || "";
          const schemaName = pickVal(sqlEntry, ["schemaName"]) || "";
          const tablesSelectedCsv = pickVal(sqlEntry, ["tablesSelected"]) || "";
          const viewsSelectedCsv = pickVal(sqlEntry, ["viewsSelected"]) || "";

          setTestConnectionPayload((prev) => ({
            ...prev,
            databaseType,
            serverName:
              pickVal(sqlEntry, ["serverName", "server"]) || prev.serverName,
            dbUserName:
              pickVal(sqlEntry, ["dbUserName", "dbUsername", "userName"]) ||
              prev.dbUserName,
            dbPassword:
              pickVal(sqlEntry, ["dbPassword", "password"]) || prev.dbPassword,
            portNum: String(
              pickVal(sqlEntry, ["portNum", "port"]) || prev.portNum || "",
            ),
          }));

          setConnection(String(connectionString || ""));
          setSelectedDB(String(databaseName || ""));
          setSelectedSchema(String(schemaName || ""));
          setSelectedTables(String(tablesSelectedCsv || ""));
          setSelectedViews(String(viewsSelectedCsv || ""));
          setSelectedTablesArray(parseCsv(tablesSelectedCsv));
          setSelectedViewsArray(parseCsv(viewsSelectedCsv));
          setDescription(String(pickVal(sqlEntry, ["description"]) || ""));
          const sqlPrompt =
            pickVal(sqlEntry, [
              "promptConfiguration",
              "PromptConfiguration",
              "sqlPromptConfiguration",
              "sqlPromptConfig",
              "promptConfig",
              "prompt",
            ]) ||
            findValueByKeyFragment(sqlEntry, ["sql", "prompt"]) ||
            findValueByKeyFragment(sqlEntry, ["prompt"]) ||
            findValueByKeyFragment(sqlAndFileRes, ["sql", "prompt"]) ||
            "";
          setPromptConfiguration(String(sqlPrompt || ""));

          if (databaseType && connectionString) {
            try {
              const dbRes = await sqlConfigurationService.getDB({
                databaseType,
                connectionString,
              });
              if (active) setDbData(readArray(dbRes));
            } catch {
              if (active) setDbData([]);
            }

            if (databaseName) {
              try {
                const dbInfoRes = await sqlConfigurationService.getDBInfo({
                  databaseType,
                  connectionString,
                  database: databaseName,
                });
                if (active && dbInfoRes?.data) {
                  const dbInfoData = dbInfoRes.data;
                  const nextSchemaList = readArray(dbInfoData.schemaList);
                  setDBInfo(dbInfoData);
                  setSchemaList(nextSchemaList);
                  const allTables = readArray(dbInfoData.tableList);
                  const allViews = readArray(dbInfoData.viewList);
                  if (schemaName) {
                    setTableList(
                      allTables.filter((t) => t.schemaName === schemaName),
                    );
                    setViewList(
                      allViews.filter((v) => v.schemaName === schemaName),
                    );
                  } else {
                    setTableList(allTables);
                    setViewList(allViews);
                  }
                }
              } catch {
                if (active) {
                  setTableList([]);
                  setViewList([]);
                  setSchemaList([]);
                }
              }
            }
          }
        }
        const fileConfigRows = readArray(fileConfigRes);
        const filePromptSource =
          fileConfigRows[0] || fileConfigRes?.data || fileConfigRes || {};
        const filePrompt =
          pickVal(filePromptSource, [
            "promptConfiguration",
            "PromptConfiguration",
            "filePromptConfiguration",
            "filePromptConfig",
            "promptConfig",
            "prompt",
          ]) ||
          findValueByKeyFragment(filePromptSource, ["file", "prompt"]) ||
          findValueByKeyFragment(filePromptSource, ["prompt"]) ||
          "";
        setFilePromptConfiguration(String(filePrompt || ""));
        const normalizedFiles = [
          ...fileConfigRows.map((row) => ({
            fileName:
              pickVal(row, ["fileName", "originalFileName", "name"]) || "",
            filePath: pickVal(row, ["filePath", "path"]) || "",
            configId: Number(
              pickVal(row, [
                "fileConfigID",
                "fileConfigId",
                "configID",
                "id",
              ]) || 0,
            ),
            companyID: Number(
              pickVal(row, ["companyID", "companyId"]) || companyId || 0,
            ),
            raw: row,
          })),
        ].filter((f) => f.fileName || f.filePath);
        const dedupedFiles = normalizedFiles.filter(
          (file, index, arr) =>
            arr.findIndex(
              (x) =>
                String(x.fileName).toLowerCase() ===
                  String(file.fileName).toLowerCase() &&
                String(x.filePath).toLowerCase() ===
                  String(file.filePath).toLowerCase(),
            ) === index,
        );
        setExistingUploadedFiles(dedupedFiles);
      } catch (error) {
        console.error("Failed to prefill SQL/File analytics:", error);
      }
    };

    hydrateConfigs();
    return () => {
      active = false;
    };
  }, [selectedCompanyDetails]);

  const handleDropdownOpen = () => {
    if (!loaded) getSqlDbType();
  };
  const handleChangeTestConnection = async (e) => {
    setConnectionSuccess(false);
    const fieldName = e.target.name;
    const stateKey = fieldName === "sqlDbUserName" ? "dbUserName" : fieldName === "sqlDbPassword" ? "dbPassword" : fieldName;
    setTestConnectionPayload((prev) => ({
      ...prev,
      [stateKey]: e.target.value,
    }));
  };

  const testConnection = async (payload) => {
    setViewList([]);
    setTableList([]);
    setSchemaList([]);
    setDBInfo({});
    setDbData([]);
    setConnection("");
    setSelectedDB("");
    setSelectedTables("");
    setSelectedViews("");
    setSelectedSchema("");
    setConnectionSuccess(false);
    try {
      const response =
        await sqlConfigurationService.getConnectionstring(payload);
      setConnection(response.data.connectionString);
      const db_data = await sqlConfigurationService.getDB({
        databaseType: payload.databaseType,
        connectionString: response.data.connectionString,
      });
      if (db_data.data?.length) {
        setDbData(db_data.data);
        setConnectionSuccess(true);
      }
    } catch (error) {
      console.error("Test connection error:", error);
    }
  };

  const handleDBInfo = async (e) => {
    const selectedValue = e.target.value;
    setViewList([]);
    setTableList([]);
    setSchemaList([]);
    setSelectedTables("");
    setSelectedViews("");
    setSelectedSchema("");
    setDescription("");
    setPromptConfiguration("");
    try {
      const response = await sqlConfigurationService.getDBInfo({
        databaseType: testConnectionPayload.databaseType,
        connectionString: connection,
        database: selectedValue,
      });
      if (response.data) {
        setSchemaList(response.data.schemaList);
        setDBInfo(response.data);
        setSelectedDB(selectedValue);
      }
    } catch (error) {
      console.error("Get DB info error:", error);
    }
  };

  const handleSchemaInfo = (e) => {
    setTableList(
      DBInfo.tableList.filter((t) => t.schemaName === e.target.value),
    );
    setViewList(DBInfo.viewList.filter((v) => v.schemaName === e.target.value));
    setSelectedSchema(e.target.value);
  };

  const handleCheckboxChangeForTables = (value) => {
    let updated = selectedTablesArray.includes(value)
      ? selectedTablesArray.filter((v) => v !== value)
      : [...selectedTablesArray, value];
    setSelectedTablesArray(updated);
    setSelectedTables(updated.join(","));
  };

  const handleCheckboxChangeForViews = (value) => {
    let updated = selectedViewsArray.includes(value)
      ? selectedViewsArray.filter((v) => v !== value)
      : [...selectedViewsArray, value];
    setSelectedViewsArray(updated);
    setSelectedViews(updated.join(","));
  };

  const updateSQLConfiguration = async () => {
    try {
      const resolvedCompanyId = getResolvedCompanyId();
      if (!resolvedCompanyId) {
        console.error("Missing companyId while saving SQL configuration");
        return;
      }
      const objectToSave = {
        ...testConnectionPayload,
        databaseName: selectedDB,
        connectionstring: connection,
        schemaName: selectedSchema,
        tablesSelected: selectedTables,
        viewsSelected: selectedViews,
        description,
        promptConfiguration,
        companyID: resolvedCompanyId,
        companyId: resolvedCompanyId,
        flgSave: 1,
        updatedBy: Number(localStorage.getItem("userId")),
      };
      await sqlConfigurationService.updateSQLConfig(objectToSave);
      setStep(4);
    } catch (error) {
      console.error("Update SQL configuration error:", error);
    }
  };

  // ==================== FILE ANALYTICS FUNCTIONS ====================
  const isCloudFormValid = () => {
    if (cloudSource === "s3")
      return !!(
        cloudConfig.bucket.trim() &&
        cloudConfig.region.trim() &&
        cloudConfig.accessKey.trim() &&
        cloudConfig.secretKey.trim()
      );
    if (cloudSource === "azure")
      return !!(
        cloudConfig.containerName.trim() && cloudConfig.connectionString.trim()
      );
    if (cloudSource === "sftp")
      return !!(
        cloudConfig.host.trim() &&
        cloudConfig.port.trim() &&
        cloudConfig.username.trim() &&
        cloudConfig.password.trim()
      );
    return false;
  };

  const buildCloudPayload = () => {
    if (cloudSource === "s3")
      return {
        storageType: "AWS S3",
        folderName: cloudConfig.folderName,
        awsAccessKey: cloudConfig.accessKey,
        awsSecretKey: cloudConfig.secretKey,
        awsBucketName: cloudConfig.bucket,
        awsRegion: cloudConfig.region,
      };
    if (cloudSource === "azure")
      return {
        storageType: "Azure",
        folderName: cloudConfig.folderName,
        azureConnectionString: cloudConfig.connectionString,
        azureContainerName: cloudConfig.containerName,
      };
    if (cloudSource === "sftp")
      return {
        storageType: "SFTP",
        folderName: cloudConfig.folderName,
        sftpHost: cloudConfig.host,
        sftpPort: Number(cloudConfig.port) || 0,
        sftpUsername: cloudConfig.username,
        sftpPassword: cloudConfig.password,
      };
    return {};
  };

  const handleTestCloudConnection = async () => {
    setCloudConnectionStatus("testing");
    setCloudFiles([]);
    setSelectedCloudFiles([]);
    try {
      const payload = buildCloudPayload();
      const res = await fileConfigurationService.getCloudFileList(payload);
      const files = res?.files || res?.data?.files || [];
      if (res.success) {
        setCloudFiles(files);
        setCloudConnectionStatus("success");
      } else {
        setCloudConnectionStatus("error");
        setCloudConnectionErrorMessage(res.message);
      }
    } catch {
      setCloudConnectionStatus("error");
    }
  };

  const toggleCloudFile = (file) =>
    setSelectedCloudFiles((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file],
    );
  const toggleAllCloudFiles = () =>
    setSelectedCloudFiles((prev) =>
      prev.length === cloudFiles.length ? [] : [...cloudFiles],
    );

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeFile = (index) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const removeExistingFile = async (index) => {
    const target = existingUploadedFiles[index];
    if (!target) return;
    const resolvedCompanyId = getResolvedCompanyId();
    if (target.configId) {
      try {
        await fileConfigurationService.deleteFileConfig({
          fileConfigID: Number(target.configId),
          fileConfigId: Number(target.configId),
          configID: Number(target.configId),
          id: Number(target.configId),
          companyID: Number(target.companyID || resolvedCompanyId || 0),
          companyId: Number(target.companyID || resolvedCompanyId || 0),
          fileName: target.fileName || "",
          filePath: target.filePath || "",
          deletedBy: Number(localStorage.getItem("userId") || 0),
          updatedBy: Number(localStorage.getItem("userId") || 0),
        });
      } catch (e) {
        console.error("Error deleting file config:", e);
        return;
      }
    }
    setExistingUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveFileConfiguration = async () => {
    try {
      const resolvedCompanyId = getResolvedCompanyId();
      if (!resolvedCompanyId) {
        console.error("Missing companyId while saving file configuration");
        return;
      }
      if (cloudSource && selectedCloudFiles.length > 0) {
        const userId = Number(localStorage.getItem("userId") || 0);
        const cloudBase = {
          companyID: resolvedCompanyId,
          companyName: companyName,
          storageType:
            cloudSource === "s3"
              ? "AWS S3"
              : cloudSource === "azure"
                ? "Azure"
                : "SFTP",
          folderName: cloudConfig.folderName,
          files: selectedCloudFiles.map((f) => ({
            fileId: 0,
            fileName: typeof f === "string" ? f : f.fileUrl || "",
            filePath: typeof f === "string" ? f : f.fileUrl || "",
            description: "",
          })),
          promptConfiguration: filePromptConfiguration,
          awsAccessKey: cloudConfig.accessKey || "",
          awsSecretKey: cloudConfig.secretKey || "",
          awsBucketName: cloudConfig.bucket || "",
          awsRegion: cloudConfig.region || "",
          azureConnectionString: cloudConfig.connectionString || "",
          azureContainerName: cloudConfig.containerName || "",
          sftpHost: cloudConfig.host || "",
          sftpPort: Number(cloudConfig.port) || 0,
          sftpUsername: cloudConfig.username || "",
          sftpPassword: cloudConfig.password || "",
        };
        await fileConfigurationService.saveCloudFile({
          ...cloudBase,
          createdBy: userId,
        });
      }
      const existingConfigId = existingUploadedFiles[0]?.configId || 0;
      if (uploadedFiles.length === 0 && !existingConfigId) {
        setStep(5);
        return;
      }
      const formData = {
        PromptConfiguration: filePromptConfiguration,
        CompanyID: resolvedCompanyId,
        companyId: resolvedCompanyId,
        CompanyName: companyName,
        CreatedBy: Number(localStorage.getItem("userId")),
        UpdatedBy: Number(localStorage.getItem("userId")),
      };
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null) data.append(key, formData[key]);
      });
      uploadedFiles.forEach((file) => data.append("files", file));
      let response;
      if (existingConfigId)
        response = await fileConfigurationService.updateFileConfig(
          data,
          existingConfigId,
        );
      else response = await fileConfigurationService.saveFile(data);
      if (response) setStep(5);
    } catch (error) {
      console.log("Error adding files:", error);
    }
  };
  const handleFinishAndStartIngestion = async () => {
    setIsStartingIngestion(true);
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      setSeedStartError(
        "Company ID is missing. Please save company details again.",
      );
      setIsStartingIngestion(false);
      return;
    }
    try {
      setSeedStartError("");
      if (selectedTablesArray.length || selectedViewsArray.length)
        await dataSeedingService.startDbSeed(resolvedCompanyId);
      if ([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length > 0)
        await dataSeedingService.startPdfSeed(resolvedCompanyId);
      if (uploadedMedia.length)
        await dataSeedingService.startMediaSeed(resolvedCompanyId);
    } catch (error) {
      console.error("Failed to start data seeding:", error);
      setSeedStartError(
        error?.message || "Failed to start data seeding. Please try again.",
      );
    } finally {
      setTimeout(() => {
        setIsStartingIngestion(false);
        localStorage.removeItem("onboardingCompanyId");
      }, 2000);
    }
  };
  const handleMediaSeeding = async () => {
    setIsStartingMediaIngestion(true);
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      setSeedStartError(
        "Company ID is missing. Please save company details again.",
      );
      setIsStartingMediaIngestion(false);
      return;
    }
    try {
      setSeedStartError("");
      if (uploadedMedia.length)
        await dataSeedingService.startMediaSeed(resolvedCompanyId);
    } catch (error) {
      console.error("Failed to start media seeding:", error);
      setSeedStartError(
        error?.message || "Failed to start media seeding. Please try again.",
      );
    } finally {
      setTimeout(() => {
        setIsStartingMediaIngestion(false);
        localStorage.removeItem("onboardingCompanyId");
      }, 2000);
    }
  };
  const handleDBSeeding = async () => {
    setIsStartingDBIngestion(true);
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      setSeedStartError(
        "Company ID is missing. Please save company details again.",
      );
      setIsStartingDBIngestion(false);
      return;
    }
    try {
      setSeedStartError("");
      if (selectedTablesArray.length || selectedViewsArray.length)
        await dataSeedingService.startDbSeed(resolvedCompanyId);
    } catch (error) {
      console.error("Failed to start db seeding:", error);
      setSeedStartError(
        error?.message || "Failed to start db seeding. Please try again.",
      );
    } finally {
      setTimeout(() => {
        setIsStartingDBIngestion(false);
        localStorage.removeItem("onboardingCompanyId");
      }, 2000);
    }
  };

  const handleFileSeeding = async () => {
    setIsStartingFileIngestion(true);
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      setSeedStartError(
        "Company ID is missing. Please save company details again.",
      );
      setIsStartingFileIngestion(false);
      return;
    }
    try {
      setSeedStartError("");
      if (
        [...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length > 0
      )
        await dataSeedingService.startPdfSeed(resolvedCompanyId);
    } catch (error) {
      console.error("Failed to start file seeding:", error);
      setSeedStartError(
        error?.message || "Failed to start file seeding. Please try again.",
      );
    } finally {
      setTimeout(() => {
        setIsStartingFileIngestion(false);
        localStorage.removeItem("onboardingCompanyId");
      }, 2000);
    }
  };

  // ==================== AUDIO/VIDEO ANALYTICS FUNCTIONS ====================
  const fetchExistingMedia = async () => {
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      console.warn("⚠️ No company ID found, skipping fetch");
      return;
    }
    setIsLoadingMedia(true);
    try {
      const response =
        await mediaService.getMediaByCompanyId(resolvedCompanyId);
      const mediaList = response?.data || [];
      if (mediaList.length > 0)
        console.log(`📁 Loaded ${mediaList.length} existing media files`);
      const formattedMedia = mediaList.map((item) => ({
        id: item.id,
        name: item.originalFileName,
        size: item.fileSize,
        type: item.contentType,
        filePath: item.filePath,
        serverId: item.id,
        uploadStatus: "completed",
        serverData: item,
      }));
      setUploadedMedia(formattedMedia);
    } catch (error) {
      console.error("❌ Failed to fetch existing media:", error.message);
    } finally {
      setIsLoadingMedia(false);
    }
  };

  const handleMediaSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    const veryLargeFiles = files.filter(
      (file) => file.size > 1024 * 1024 * 1024,
    );
    if (veryLargeFiles.length > 0) {
      const warning = veryLargeFiles
        .map((f) => `${f.name} (${(f.size / 1024 / 1024 / 1024).toFixed(2)}GB)`)
        .join(", ");
      if (
        !window.confirm(
          `⚠️ Warning: Large files detected:\n${warning}\n\nContinue upload?`,
        )
      ) {
        event.target.value = "";
        return;
      }
    }
    console.log(`📁 Uploading ${files.length} file(s)`);
    const newFiles = files.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadStatus: "uploading",
      uploadProgress: 0,
    }));
    setUploadedMedia((prev) => [...prev, ...newFiles]);
    event.target.value = "";
    const startIndex = uploadedMedia.length;
    for (let i = 0; i < newFiles.length; i++)
      await uploadSingleFile(newFiles[i], startIndex + i);
    console.log("✅ All uploads completed");
  };

  const uploadSingleFile = async (mediaItem, index) => {
    const fileSizeMB = (mediaItem.file.size / 1024 / 1024).toFixed(2);
    console.log(`📤 Uploading: ${mediaItem.name} (${fileSizeMB} MB)`);
    try {
      setUploadedMedia((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, uploadStatus: "uploading", uploadProgress: 0 }
            : item,
        ),
      );
      const resolvedCompanyId = getResolvedCompanyId();
      const createdBy = Number(localStorage.getItem("userId") || 0);

      if (!resolvedCompanyId) throw new Error("Company ID not found");
      const startTime = Date.now();
      const response = await mediaService.uploadMedia(
        mediaItem.file,
        createdBy,
        resolvedCompanyId,
        companyName,
        (progress) => {
          setUploadProgress(progress);
          setUploadedMedia((prev) =>
            prev.map((item, i) =>
              i === index ? { ...item, uploadProgress: progress } : item,
            ),
          );
        },
      );
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(`✅ Uploaded: ${mediaItem.name} (${duration}s)`, response);
      setUploadedMedia((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                uploadStatus: "completed",
                serverId: response.id,
                serverData: response,
                uploadProgress: 100,
              }
            : item,
        ),
      );
      setUploadMediaStatus(true);
      showToast(`${mediaItem.name} uploaded successfully`, "success");
    } catch (error) {
      console.error(`❌ Failed: ${mediaItem.name} - ${error.message}`);
      setUploadedMedia((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, uploadStatus: "error", error: error.message }
            : item,
        ),
      );
      showToast(
        `Failed to upload ${mediaItem.name}: ${error.message}`,
        "error",
      );
    }
  };

  const handleDeleteMediaClick = (index, fileName) =>
    setDeleteMediaConfirm({ index, fileName });

  const confirmDeleteMedia = async () => {
    if (!deleteMediaConfirm) return;
    const { index, fileName } = deleteMediaConfirm;
    const mediaToRemove = uploadedMedia[index];
    if (mediaToRemove.serverId) {
      console.log(`🗑️ Deleting: ${fileName}`);
      try {
        const deletedBy = Number(localStorage.getItem("userId") || 0);
        await mediaService.deleteMediaFile(mediaToRemove.serverId, deletedBy);
        console.log(`✅ Deleted: ${fileName}`);
        showToast(`${fileName} deleted successfully`, "success");
      } catch (error) {
        console.error(`❌ Delete failed: ${fileName}`, error.message);
        showToast(`Failed to delete ${fileName}`, "error");
        setDeleteMediaConfirm(null);
        return;
      }
    }
    setUploadedMedia((prev) => prev.filter((_, i) => i !== index));
    setDeleteMediaConfirm(null);
  };

  const saveMediaConfiguration = async () => {
    const pendingFiles = uploadedMedia.filter(
      (f) => f.uploadStatus !== "completed",
    );
    if (pendingFiles.length > 0) {
      console.warn(`⚠️ ${pendingFiles.length} file(s) still uploading`);
      showToast(
        `Please wait for ${pendingFiles.length} file(s) to finish uploading`,
        "error",
      );
      return;
    }
    const mediaIds = uploadedMedia.map((f) => f.serverId).filter((id) => id);
    console.log(
      `✅ Configuration saved. ${mediaIds.length} media file(s) ready`,
    );
    setStep(6);
  };

  const getMlModelCardTemplate = () => ({
    localId: `ml_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    modelName: "",
    preferredModel: "",
    description: "",
    sourceId: "",
    sourcePath: "",
    timeColumn: "",
    targetColumn: "",
    featureColumns: [],
  });

  const getMlRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.items)) return payload.items;
    const visited = new Set();
    const walk = (node, depth = 0) => {
      if (!node || depth > 6) return [];
      if (Array.isArray(node)) return node;
      if (typeof node !== "object") return [];
      if (visited.has(node)) return [];
      visited.add(node);
      for (const value of Object.values(node)) {
        const found = walk(value, depth + 1);
        if (Array.isArray(found) && found.length > 0) return found;
      }
      return [];
    };
    const foundRows = walk(payload);
    if (Array.isArray(foundRows) && foundRows.length > 0) return foundRows;
    return [];
  };

  const extractMlColumnsFromDataset = (dataset) => {
    const directArray =
      pickVal(dataset, ["columns", "columnNames", "featureColumns", "sourceColumns"]) || [];
    if (Array.isArray(directArray) && directArray.length > 0) return directArray;
    const csvValue =
      pickVal(dataset, ["columns", "columnNames", "headers", "featureColumns", "sourceColumns"]) || "";
    const fromJson = parseJsonArray(csvValue);
    if (fromJson.length > 0) return fromJson;
    if (typeof csvValue === "string") return parseCsv(csvValue);
    return [];
  };

  const getMlModelId = (model = {}) =>
    Number(pickVal(model, ["mlModelId", "modelId", "id"]) || 0);

  const getMlFeatureColumns = (model = {}) => {
    const raw = pickVal(model, [
      "featureColumns",
      "independentVariables",
      "features",
      "sourceColumns",
    ]);
    if (Array.isArray(raw)) return raw;
    const parsed = parseJsonArray(raw);
    if (parsed.length > 0) return parsed;
    if (typeof raw === "string") return parseCsv(raw);
    return [];
  };

  const toMlCardFromModel = (model = {}) => ({
    localId: `ml_edit_${Date.now()}`,
    modelName: String(
      pickVal(model, ["mlModelName", "modelName", "name"]) || "",
    ),
    preferredModel: String(
      pickVal(model, ["preferredModel", "mlModelType", "modelType", "algorithm"]) || "",
    ),
    description: String(pickVal(model, ["description", "details"]) || ""),
    sourceId: String(pickVal(model, ["sourceId", "datasetId"]) || ""),
    sourcePath: String(pickVal(model, ["filePath", "sourcePath"]) || ""),
    timeColumn: String(pickVal(model, ["timeColumn", "xAxis"]) || ""),
    targetColumn: String(pickVal(model, ["targetColumn", "yAxis"]) || ""),
    featureColumns: getMlFeatureColumns(model),
  });

  const getDatasetDisplayName = (dataset = {}, fallback = "Unknown Source") =>
    String(
      pickVal(dataset, ["originalFileName", "fileName", "sourceName", "name"]) ||
        fallback,
    );

  const normalizeMlStatus = (value) => String(value || "").trim().toLowerCase();

  const hydrateMlData = async () => {
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) return;

    try {
      const [typesRes, dataSetsRes, modelsRes] = await Promise.all([
        mlService.getAllMLModelTypes().catch(() => []),
        mlService.getAllDataSetsByCompanyId(resolvedCompanyId).catch(() => []),
        mlService.getAllMLModelsByCompanyId(resolvedCompanyId).catch(() => []),
      ]);

      const normalizedTypes = getMlRows(typesRes).map((item) => ({
        value:
          getDisplayName(item, ["value", "modelType", "type", "name", "title"]) ||
          String(item || ""),
        label:
          getDisplayName(item, ["label", "name", "modelType", "type", "title"]) ||
          String(item || ""),
      }));
      setMlModelTypes(normalizedTypes.filter((item) => item.value));

      const normalizedDataSets = getMlRows(dataSetsRes);
      setMlDataSets(normalizedDataSets);

      if (normalizedDataSets.length > 0) {
        const firstDataset = normalizedDataSets[0];
        const firstSourceId =
          pickVal(firstDataset, ["sourceId", "id", "datasetId"]) || "";
        const firstFilePath =
          pickVal(firstDataset, ["filePath", "path", "sourcePath"]) || "";
        setSelectedDataSetId(String(firstSourceId || ""));
        setSelectedDataSetPath(String(firstFilePath || ""));
        const inferredColumns = extractMlColumnsFromDataset(firstDataset);
        if (inferredColumns.length > 0) setMlColumns(inferredColumns);
      }

      const normalizedModels = getMlRows(modelsRes);
      setMlExistingModels(normalizedModels);
    } catch (error) {
      console.error("Failed to hydrate ML data:", error);
    }
  };

  useEffect(() => {
    if (step === 6) hydrateMlData();
  }, [step, CompanyID, selectedCompanyDetails]);

  useEffect(() => {
    if (step !== 6) return;
    const hasRunning = mlExistingModels.some(
      (model) => normalizeMlStatus(pickVal(model, ["mlModelStatus", "status"])) === "running",
    );
    if (!hasRunning) return;

    const timer = setInterval(() => {
      hydrateMlData().catch(() => null);
    }, 5000);
    return () => clearInterval(timer);
  }, [step, mlExistingModels]);

  useEffect(() => {
    const applyResponsiveSidebar = () => {
      if (typeof window === "undefined") return;
      setIsAutoSidebarCollapsed(
        window.innerWidth < SIDEBAR_AUTO_COLLAPSE_BREAKPOINT,
      );
    };
    applyResponsiveSidebar();
    window.addEventListener("resize", applyResponsiveSidebar);
    return () => window.removeEventListener("resize", applyResponsiveSidebar);
  }, []);

  const toggleSidebarCollapse = () => {
    setManualSidebarCollapsed((prev) => {
      const current =
        typeof prev === "boolean" ? prev : isAutoSidebarCollapsed;
      return !current;
    });
  };

  const handleMlDataSetSelect = (event) => {
    const nextId = event.target.value;
    setSelectedDataSetId(nextId);
    const selected = mlDataSets.find(
      (item) =>
        String(pickVal(item, ["sourceId", "id", "datasetId"])) ===
        String(nextId),
    );
    setSelectedDataSetPath(
      String(pickVal(selected, ["filePath", "path", "sourcePath"]) || ""),
    );
    const inferredColumns = extractMlColumnsFromDataset(selected);
    if (inferredColumns.length > 0) setMlColumns(inferredColumns);
  };

  const handleMlDatasetUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const resolvedCompanyId = getResolvedCompanyId();
    if (!resolvedCompanyId) {
      showToast("Please save company details first.", "error");
      return;
    }

    setIsUploadingMlDataset(true);
    try {
      const text = await file.text();
      const headers = normalizeCsvHeaders(text);
      setMlColumns(headers);

      const uploadResponse = await mlService.uploadDataSet(resolvedCompanyId, file);
      const uploadRows = getMlRows(uploadResponse);
      if (uploadRows.length > 0) {
        setMlDataSets((prev) => {
          const merged = [...uploadRows, ...prev];
          const seen = new Set();
          return merged.filter((item, idx) => {
            const key =
              String(pickVal(item, ["sourceId", "id", "datasetId"]) || "") +
              "::" +
              String(
                pickVal(item, ["fileName", "sourceName", "name", "filePath"]) || idx,
              );
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      } else {
        setMlDataSets((prev) => [
          {
            sourceId: `local_${Date.now()}`,
            fileName: file.name,
            filePath: file.name,
          },
          ...prev,
        ]);
      }
      showToast("Dataset uploaded successfully.");
      await hydrateMlData();
    } catch (error) {
      console.error("ML dataset upload error:", error);
      showToast("Failed to upload dataset. Please try again.", "error");
    } finally {
      setIsUploadingMlDataset(false);
    }
  };

  const addMlModelCard = () =>
    setMlModelCards((prev) => [
      ...prev,
      {
        ...getMlModelCardTemplate(),
        sourceId: String(selectedDataSetId || ""),
        sourcePath: String(selectedDataSetPath || ""),
      },
    ]);

  const removeMlModelCard = (localId) =>
    setMlModelCards((prev) => prev.filter((card) => card.localId !== localId));

  const setMlModelCardField = (localId, field, value) =>
    setMlModelCards((prev) =>
      prev.map((card) =>
        card.localId === localId ? { ...card, [field]: value } : card,
      ),
    );

  const toggleFeatureColumn = (localId, columnName) =>
    setMlModelCards((prev) =>
      prev.map((card) => {
        if (card.localId !== localId) return card;
        const exists = card.featureColumns.includes(columnName);
        return {
          ...card,
          featureColumns: exists
            ? card.featureColumns.filter((c) => c !== columnName)
            : [...card.featureColumns, columnName],
        };
      }),
    );

  const handleMlCardSourceChange = (localId, sourceId) => {
    const selected = mlDataSets.find(
      (item) =>
        String(pickVal(item, ["sourceId", "id", "datasetId"])) ===
        String(sourceId),
    );
    const sourcePath = String(
      pickVal(selected, ["filePath", "path", "sourcePath"]) || "",
    );
    const inferredColumns = extractMlColumnsFromDataset(selected);
    if (inferredColumns.length > 0) setMlColumns(inferredColumns);

    setMlModelCards((prev) =>
      prev.map((card) =>
        card.localId === localId
          ? {
              ...card,
              sourceId: String(sourceId || ""),
              sourcePath,
              timeColumn: "",
              targetColumn: "",
              featureColumns: [],
            }
          : card,
      ),
    );
  };

  const handleEditExistingMlModel = (model) => {
    const modelId = getMlModelId(model);
    if (!modelId) {
      showToast("Model id not found for selected row.", "error");
      return;
    }
    const sourceId = String(pickVal(model, ["sourceId", "datasetId"]) || "");
    const matchedDataSet = mlDataSets.find(
      (item) =>
        String(pickVal(item, ["sourceId", "id", "datasetId"])) === sourceId,
    );
    const filePath =
      pickVal(matchedDataSet, ["filePath", "path", "sourcePath"]) ||
      pickVal(model, ["filePath", "sourcePath"]) ||
      "";
    if (sourceId) setSelectedDataSetId(sourceId);
    if (filePath) setSelectedDataSetPath(String(filePath));

    const inferredColumns =
      extractMlColumnsFromDataset(matchedDataSet).length > 0
        ? extractMlColumnsFromDataset(matchedDataSet)
        : extractMlColumnsFromDataset(model);
    if (inferredColumns.length > 0) setMlColumns(inferredColumns);

    const nextCard = toMlCardFromModel(model);
    nextCard.sourcePath = String(filePath || nextCard.sourcePath || "");
    setMlModelCards([nextCard]);
    setEditingMlModelId(modelId);
    showToast("Model loaded for edit.");
  };

  const handleDeleteExistingMlModel = async (model) => {
    const resolvedCompanyId = getResolvedCompanyId();
    const deletedBy = Number(localStorage.getItem("userId") || 0);
    const modelId = getMlModelId(model);

    if (!resolvedCompanyId || !modelId) {
      showToast("Unable to delete model. Missing model/company id.", "error");
      return;
    }

    const modelName = String(
      pickVal(model, ["mlModelName", "modelName", "name"]) || "this model",
    );
    if (!window.confirm(`Delete ${modelName}?`)) return;

    setIsDeletingMlModel(true);
    try {
      await mlService.deleteMLModelById(resolvedCompanyId, modelId, deletedBy);
      if (editingMlModelId === modelId) {
        setEditingMlModelId(null);
        setMlModelCards([]);
      }
      showToast("Model deleted successfully.");
      await hydrateMlData();
    } catch (error) {
      console.error("Delete ML model error:", error);
      showToast("Failed to delete model.", "error");
    } finally {
      setIsDeletingMlModel(false);
    }
  };

  const handleBuildExistingMlModel = async (model) => {
    const resolvedCompanyId = getResolvedCompanyId();
    const modelId = getMlModelId(model);
    if (!resolvedCompanyId || !modelId) {
      showToast("Unable to build model. Missing model/company id.", "error");
      return;
    }

    setBuildingMlModelIds((prev) =>
      prev.includes(modelId) ? prev : [...prev, modelId],
    );
    try {
      await mlService.buildMLModel(resolvedCompanyId, modelId);
      setMlExistingModels((prev) =>
        prev.map((item) =>
          getMlModelId(item) === modelId
            ? { ...item, mlModelStatus: "Running" }
            : item,
        ),
      );
      showToast("Model build started.");
      setTimeout(() => {
        hydrateMlData().catch(() => null);
      }, 2500);
    } catch (error) {
      console.error("Build ML model error:", error);
      showToast("Failed to start model build.", "error");
    } finally {
      setBuildingMlModelIds((prev) => prev.filter((id) => id !== modelId));
    }
  };

  const validateMlCard = (card) => {
    if (!String(card.sourceId || "").trim()) return false;
    if (!String(card.modelName || "").trim()) return false;
    if (!String(card.preferredModel || "").trim()) return false;
    if (!String(card.targetColumn || "").trim()) return false;
    if (!Array.isArray(card.featureColumns) || card.featureColumns.length === 0)
      return false;
    return true;
  };

  const handleSaveMlModels = async () => {
    const resolvedCompanyId = getResolvedCompanyId();
    const createdBy = Number(localStorage.getItem("userId") || 0);
    if (!resolvedCompanyId) {
      showToast("Please save company details first.", "error");
      return;
    }
    if (!mlModelCards.length) {
      showToast("Click Add New Model..... to open the model form.", "error");
      return;
    }
    const invalid = mlModelCards.find((card) => !validateMlCard(card));
    if (invalid) {
      showToast("Please fill all required model fields.", "error");
      return;
    }

    setIsSavingMlModels(true);
    try {
      if (editingMlModelId) {
        // No dedicated update endpoint is available, so update via delete+create.
        await mlService.deleteMLModelById(
          resolvedCompanyId,
          editingMlModelId,
          createdBy,
        );
      }

      for (const card of mlModelCards) {
        const sourceIdToUse = Number(card.sourceId || selectedDataSetId || 0);
        await mlService.saveMLModel({
          createdBy,
          sourceId: sourceIdToUse,
          companyId: resolvedCompanyId,
          mlModelName: card.modelName,
          timeColumn: card.timeColumn,
          featureColumns: card.featureColumns,
          targetColumn: card.targetColumn,
          description: card.description,
          preferredModel: card.preferredModel,
        });
      }
      showToast(
        editingMlModelId
          ? "Model updated. Click Build to start training."
          : "Model(s) saved. Click Build to start training.",
      );
      await hydrateMlData();
      setMlModelCards([]);
      setEditingMlModelId(null);
    } catch (error) {
      console.error("Save ML models error:", error);
      showToast("Failed to save prediction model(s).", "error");
    } finally {
      setIsSavingMlModels(false);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col md:flex-row overflow-hidden" style={{ height: 'calc(100vh - 10rem)' }}>
        {/* Left Sidebar */}
        <div
          className={`relative w-full md:flex-shrink-0 bg-slate-50 dark:bg-slate-900/40 border-r border-slate-100 dark:border-slate-700 flex flex-col overflow-hidden ${
            isSidebarCollapsed ? "md:w-32 p-8 sm:p-10" : "md:w-80 p-8 sm:p-10"
          }`}
        >
          <button
            type="button"
            onClick={toggleSidebarCollapse}
            title={
              isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
            className={`hidden md:inline-flex absolute z-20 top-4 w-8 h-8 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 ${
              "hover:bg-white dark:hover:bg-slate-700"
            }`}
            style={{
              left: isSidebarCollapsed ? "calc(50% - 1rem)" : "calc(100% - 3rem)",
            }}
          >
            {isSidebarCollapsed ? <ChevronRight size={14} /> : <ArrowLeft size={14} />}
          </button>

          <div className="h-[70px] overflow-hidden">
            <div
              className={`${
                isSidebarCollapsed
                  ? "opacity-0 -translate-y-1 pointer-events-none"
                  : "opacity-100 translate-y-0"
              }`}
              aria-hidden={isSidebarCollapsed}
            >
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Tenant Setup
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Configure a new enterprise instance
              </p>
              {viewOnly && (
                <div className="mt-4 flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                  <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                    View Only
                  </span>
                  <button
                    onClick={onEnableEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#1d57a5] hover:bg-[#164280] text-white rounded-lg transition-colors"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                </div>
              )}
            </div>
          </div>
          <StepStepper
            steps={STEPS}
            currentStep={step}
            compact={isSidebarCollapsed}
            collapsed={isSidebarCollapsed}
          />
        </div>

        {/* Right Content */}
        <div className="flex-1 bg-white dark:bg-slate-800 relative overflow-y-auto flex flex-col">
          <div className={`flex-1 p-8 sm:p-12 overflow-y-auto hide-scrollbar`}>
            {/* ==================== STEP 1: COMPANY PROFILE ==================== */}
            {step === 1 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                    Company Profile
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400">
                     Provide the official registration and operational details for
                  the new tenant.
                  </p>
                </div>
                
                <div
                  className={`space-y-12 ${viewOnly ? "pointer-events-none opacity-100 select-none" : ""}`}
                >
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Building2
                        size={16}
                        className="text-[#1d57a5] dark:text-[#4a8ce3]"
                      />{" "}
                      General Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          className={inputCls}
                        />
                        {errors.companyName && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.companyName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          Business/Trade Name{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="businessTradeName"
                          value={formData.businessTradeName}
                          onChange={handleChange}
                          className={inputCls}
                        />
                        {errors.businessTradeName && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.businessTradeName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          Industry / Business Type{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="industryType"
                          value={formData.industryType}
                          onChange={handleChange}
                          placeholder="e.g. Fintech, Healthcare"
                          className={inputCls + " placeholder-slate-400"}
                        />
                        {errors.industryType && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.industryType}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>Company Size</label>
                        <input
                          type="text"
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleChange}
                          placeholder="e.g. 50-200 employees"
                          className={inputCls + " placeholder-slate-400"}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelCls}>Description / About</label>
                        <textarea
                          rows="2"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          className={inputCls + " resize-none"}
                        />
                      </div>
                    </div>
                  </section>
                  <section>
                    <h4 className={sectionHeadCls}>
                      <FileText size={16} className="text-[#36a852]" />{" "}
                      Registration &amp; Tax Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>
                          Registration Number{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="companyRegistrationNumber"
                          value={formData.companyRegistrationNumber}
                          onChange={handleChange}
                          className={inputCls + " font-mono"}
                        />
                        {errors.companyRegistrationNumber && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.companyRegistrationNumber}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          Date of Incorporation
                        </label>
                        <input
                          type="date"
                          name="dateOfIncorporation"
                          value={formData.dateOfIncorporation}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>
                          GST Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="gstNo"
                          value={formData.gstNo}
                          onChange={handleChange}
                          className={inputCls + " font-mono uppercase"}
                        />
                        {errors.gstNo && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.gstNo}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          PAN Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="panNo"
                          value={formData.panNo}
                          onChange={handleChange}
                          className={inputCls + " font-mono uppercase"}
                        />
                        {errors.panNo && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.panNo}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          TAN Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="tanNo"
                          value={formData.tanNo}
                          onChange={handleChange}
                          className={inputCls + " font-mono uppercase"}
                        />
                        {errors.tanNo && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.tanNo}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Network size={16} className="text-purple-500" /> Contact
                      Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Primary Phone</label>
                        <input
                          type="tel"
                          name="primaryPhone"
                          value={formData.primaryPhone}
                          onChange={handleChange}
                          placeholder="+1 (555) 000-0000"
                          className={inputCls + " placeholder-slate-400"}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Company Website</label>
                        <input
                          type="url"
                          name="companyWebsite"
                          value={formData.companyWebsite}
                          onChange={handleChange}
                          placeholder="https://"
                          className={inputCls + " placeholder-slate-400"}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>
                          Official Email Address{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="officialEmail"
                          value={formData.officialEmail}
                          onChange={handleChange}
                          placeholder="hello@company.com"
                          className={inputCls + " placeholder-slate-400"}
                        />
                        {errors.officialEmail && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.officialEmail}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={labelCls}>
                          Alternate Contact Number
                        </label>
                        <input
                          type="tel"
                          name="alternateContact"
                          value={formData.alternateContact}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelCls}>Registered Address</label>
                        <input
                          type="text"
                          name="registeredAddress"
                          value={formData.registeredAddress}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelCls}>
                          Corporate Address (if different)
                        </label>
                        <input
                          type="text"
                          name="corporateAddress"
                          value={formData.corporateAddress}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </section>
                  <section>
                    <h4 className={sectionHeadCls}>
                      <User size={16} className="text-amber-500" /> Key
                      Personnel (Admin)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Contact Person Name</label>
                        <input
                          type="text"
                          name="contactPersonName"
                          value={formData.contactPersonName}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Designation / Role</label>
                        <input
                          type="text"
                          name="designationRole"
                          value={formData.designationRole}
                          onChange={handleChange}
                          placeholder="e.g. CTO, Operations Head"
                          className={inputCls + " placeholder-slate-400"}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Direct Work Email</label>
                        <input
                          type="email"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Direct Mobile Number</label>
                        <input
                          type="tel"
                          name="mobileNumber"
                          value={formData.mobileNumber}
                          onChange={handleChange}
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </section>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex justify-end">
                  <button
                    onClick={() =>
                      viewOnly ? setStep(2) : addBasicInfo(formData)
                    }
                    className="bg-[#1d57a5] hover:bg-[#164280] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
                  >
                    Continue to Step 2 <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* ==================== STEP 2: MODEL CONFIGURATION ==================== */}
            {step === 2 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-24">
                {/* Header */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                    Model Configuration
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400">
                    Configure LLM agents and their settings for intelligent
                    query processing.
                  </p>
                </div>

                {/* Main Content */}
                <div
                  className={`bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm`}
                >
                  {/* TOP BAR - Groups & Agent Steps */}
                  <div
                    className={`border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60`}
                  >
                    {/* Groups Row */}
                    <div className="px-5 pt-4 pb-3 flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-white">
                          Model Groups
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 flex-1 overflow-x-auto hide-scrollbar">
                        {modelGroups.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">
                            No groups yet
                          </span>
                        ) : (
                          modelGroups.map((group) => (
                            <div
                              key={group.id}
                              className={`flex items-center gap-1 rounded-lg flex-shrink-0 transition-all ${
                                activeGroupId === group.id
                                  ? "bg-[#1d57a5] shadow-md ring-2 ring-[#1d57a5]/30"
                                  : "bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-[#1d57a5]/50"
                              }`}
                            >
                              <button
                                onClick={() => handleSelectGroup(group.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  activeGroupId === group.id
                                    ? "text-white"
                                    : "text-slate-700 dark:text-slate-300 hover:text-[#1d57a5]"
                                }`}
                              >
                                {group.name}
                              </button>
                              {modelGroups.length > 1 &&
                                activeGroupId === group.id &&
                                !viewOnly && (
                                  <button
                                    onClick={handleDeleteGroup}
                                    className="p-1 mr-1 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
                                    title="Delete group"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                            </div>
                          ))
                        )}
                      </div>
                      {activeGroupId && (
                        <input
                          type="text"
                          value={getActiveGroup()?.name || ""}
                          onChange={(e) => {
                            setModelGroups((groups) =>
                              groups.map((g) =>
                                g.id === activeGroupId
                                  ? { ...g, name: e.target.value }
                                  : g,
                              ),
                            );
                            setModelSaved(false);
                          }}
                          className="w-40 flex-shrink-0 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all"
                          placeholder="Group name"
                        />
                      )}
                      <button
                        onClick={() => setShowAddGroup(true)}
                        disabled={viewOnly}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#1d57a5] hover:bg-[#164280] text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus size={13} /> Add Group
                      </button>
                    </div>

                    {/* Agent Steps Tabs */}
                    <div className="px-5 py-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0 mr-1">
                        Agents:
                      </span>
                      {availableAgents.map((agent, idx) => {
                        const getAgentIcon = (name) =>
                          ({
                            Orchestrator: <Cpu size={13} />,
                            FinalResponse: <MessageSquare size={13} />,
                            SQL: <Database size={13} />,
                            File: <FileText size={13} />,
                            Embedding: <Layers size={13} />,
                          })[name] || <Cpu size={13} />;
                        const cfg = getAgentConfig(agent.agentType);
                        const hasErrors =
                          modelErrors[`apiKey_${agent.agentType}`] ||
                          modelErrors[`modelId_${agent.agentType}`];
                        const isFilled =
                          cfg.apiKey?.trim() && cfg.modelId?.trim();
                        const isActive = activeAgentIdx === idx;
                        const isDisabled = idx > maxReachedAgentIdx;
                        return (
                          <button
                            key={agent.agentType}
                            onClick={() =>
                              !isDisabled && setActiveAgentIdx(idx)
                            }
                            disabled={isDisabled}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium flex-shrink-0 transition-all border ${
                              isDisabled ? "opacity-40 cursor-not-allowed" : ""
                            } ${
                              isActive
                                ? "bg-[#1d57a5] text-white border-[#1d57a5] shadow-md"
                                : hasErrors
                                  ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400"
                                  : isFilled
                                    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                                    : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-[#1d57a5] hover:text-[#1d57a5]"
                            }`}
                          >
                            <span className={isActive ? "text-white" : ""}>
                              {getAgentIcon(agent.name)}
                            </span>
                            {agent.name}
                            {!isActive && isFilled && !hasErrors && (
                              <CheckCircle2
                                size={12}
                                className="text-emerald-500"
                              />
                            )}
                            {!isActive && hasErrors && (
                              <AlertCircle size={12} className="text-red-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* FULL WIDTH - Agent Configuration */}
                  <div className="bg-white dark:bg-slate-800">
                    {availableAgents[activeAgentIdx] ? (
                      (() => {
                        const agent = availableAgents[activeAgentIdx];
                        const cfg = getAgentConfig(agent.agentType);
                        return (
                          <div className="p-6">
                            {/* Agent Header */}
                            <div className="mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-[#1d57a5]/10 text-[#1d57a5] flex items-center justify-center">
                                  {{
                                    Orchestrator: <Cpu size={22} />,
                                    FinalResponse: <MessageSquare size={22} />,
                                    SQL: <Database size={22} />,
                                    File: <FileText size={22} />,
                                    Embedding: <Layers size={22} />,
                                  }[agent.name] || <Cpu size={22} />}
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                    {agent.name} Agent
                                  </h3>
                                  {agent.description && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                      {agent.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Configuration Form */}
                            <div
                              className={`space-y-5 ${viewOnly ? "pointer-events-none opacity-100 select-none" : ""}`}
                            >
                              {/* API Endpoint */}
                              <div>
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                  API Endpoint{" "}
                                  <span className="text-slate-400 font-normal">
                                    (Optional)
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  value={cfg.apiEndpoint}
                                  onChange={(e) =>
                                    setAgentConfigField(
                                      agent.agentType,
                                      "apiEndpoint",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="https://api.openai.com/v1"
                                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm"
                                />
                              </div>

                              {/* API Key & Model ID */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    API Key{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="password"
                                    value={cfg.apiKey}
                                    onChange={(e) =>
                                      setAgentConfigField(
                                        agent.agentType,
                                        "apiKey",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="sk-..."
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm font-mono"
                                  />
                                  {modelErrors[`apiKey_${agent.agentType}`] && (
                                    <p className="text-xs text-red-500 mt-1">
                                      {modelErrors[`apiKey_${agent.agentType}`]}
                                    </p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    Model ID{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={cfg.modelId}
                                    onChange={(e) =>
                                      setAgentConfigField(
                                        agent.agentType,
                                        "modelId",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="gpt-4-turbo"
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm font-mono"
                                  />
                                  {modelErrors[
                                    `modelId_${agent.agentType}`
                                  ] && (
                                    <p className="text-xs text-red-500 mt-1">
                                      {
                                        modelErrors[
                                          `modelId_${agent.agentType}`
                                        ]
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Temperature & Max Tokens */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    Temperature{" "}
                                    <span className="text-slate-400 font-normal">
                                      (0-2)
                                    </span>
                                  </label>
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="range"
                                      step="0.1"
                                      min="0"
                                      max="2"
                                      value={cfg.temperature}
                                      onChange={(e) =>
                                        setAgentConfigField(
                                          agent.agentType,
                                          "temperature",
                                          parseFloat(e.target.value),
                                        )
                                      }
                                      className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#1d57a5]"
                                    />
                                    <span className="w-12 px-2 py-1 text-center text-xs font-mono bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300">
                                      {cfg.temperature}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    Max Tokens
                                  </label>
                                  <input
                                    type="number"
                                    step="100"
                                    min="1"
                                    max="4096"
                                    value={cfg.maxTokens}
                                    onChange={(e) =>
                                      setAgentConfigField(
                                        agent.agentType,
                                        "maxTokens",
                                        parseInt(e.target.value),
                                      )
                                    }
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm"
                                  />
                                </div>
                              </div>

                              {/* System Prompt */}
                              { agent.supportsSystemPrompt ? 
                                (<div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                                    System Prompt
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={cfg.useDefaultPrompt}
                                      onChange={(e) =>
                                        setAgentConfigField(
                                          agent.agentType,
                                          "useDefaultPrompt",
                                          e.target.checked,
                                        )
                                      }
                                      className="w-4 h-4 rounded border-slate-300 text-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20"
                                    />
                                    <span className="text-xs text-slate-600 dark:text-slate-400">
                                      Use Default
                                    </span>
                                  </label>
                                </div>
                                {cfg.useDefaultPrompt ? (
                                  <div className="bg-slate-100 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                      {agent.defaultPrompt}
                                    </p>
                                  </div>
                                ) : (
                                  <textarea
                                    rows="4"
                                    value={cfg.systemPrompt}
                                    onChange={(e) =>
                                      setAgentConfigField(
                                        agent.agentType,
                                        "systemPrompt",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter system instructions..."
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all resize-none overflow-y-auto text-sm"
                                  />
                                )}
                              </div>) : (null)
                              }
                            </div>

                            {/* Navigation Buttons */}
                            <div className="flex items-center justify-between pt-6 mt-6 border-t border-slate-200 dark:border-slate-700">
                              <button
                                onClick={() =>
                                  setActiveAgentIdx((i) => Math.max(0, i - 1))
                                }
                                disabled={activeAgentIdx === 0}
                                className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-[#1d57a5] disabled:opacity-30 transition-colors"
                              >
                                <ArrowLeft size={15} /> Prev Agent
                              </button>

                              {activeAgentIdx < availableAgents.length - 1 ? (
                                <div className="flex flex-col items-end gap-1">
                                  <button
                                    onClick={() => {
                                      if (modelGroups.length === 0) {
                                        setShowAddGroup(true);
                                        setIsFirstModel(true)
                                        return;
                                      }
                                      const cfg = getAgentConfig(
                                        availableAgents[activeAgentIdx]
                                          ?.agentType,
                                      );
                                      const errs = {};
                                      if (!cfg.apiKey?.trim())
                                        errs[
                                          `apiKey_${availableAgents[activeAgentIdx].agentType}`
                                        ] = "API Key is required.";
                                      if (!cfg.modelId?.trim())
                                        errs[
                                          `modelId_${availableAgents[activeAgentIdx].agentType}`
                                        ] = "Model ID is required.";
                                      if (Object.keys(errs).length) {
                                        setModelErrors((prev) => ({
                                          ...prev,
                                          ...errs,
                                        }));
                                        return;
                                      }
                                      const next = activeAgentIdx + 1;
                                      setActiveAgentIdx(next);
                                      setMaxReachedAgentIdx((prev) =>
                                        Math.max(prev, next),
                                      );
                                    }}
                                    className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#1d57a5] hover:bg-[#164280] text-white text-sm font-medium transition-all shadow-sm"
                                  >
                                    Next Agent <ChevronRight size={15} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3">
                                  {!viewOnly && modelSaved && (
                                    <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-full">
                                      <CheckCircle2 size={12} /> Saved
                                    </span>
                                  )}
                                  {viewOnly ? (
                                    <button
                                      onClick={() => setActiveAgentIdx(0)}
                                      className="flex items-center gap-2 px-6 py-2 rounded-full text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-semibold transition-all"
                                    >
                                      <ArrowLeft size={15} /> Back
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleSaveModelConfig}
                                      disabled={
                                        isSavingModel ||
                                        modelGroups.length === 0
                                      }
                                      className={`flex items-center gap-2 px-6 py-2 rounded-full text-white text-sm font-semibold transition-all shadow-sm ${
                                        modelGroups.length === 0
                                          ? "bg-slate-400 cursor-not-allowed"
                                          : "bg-[#36a852] hover:bg-[#2e9046]"
                                      }`}
                                      title={
                                        modelGroups.length === 0
                                          ? "Please create a group first"
                                          : ""
                                      }
                                    >
                                      {isSavingModel ? (
                                        <>
                                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                          Saving...
                                        </>
                                      ) : (
                                        "Save Configuration"
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="flex items-center justify-center h-full min-h-[400px]">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-3">
                            <Cpu size={32} className="text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-500">
                            Select an agent to configure
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Group Modal */}
                {showAddGroup && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md shadow-xl">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-[#1d57a5]/10 text-[#1d57a5] flex items-center justify-center">
                          <Layers size={20} />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-800 dark:text-white">
                            Add New Model Group
                          </h3>
                          <p className="text-xs text-slate-500">
                            Create a group to organize your agents
                          </p>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="Enter group name"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm mb-5"
                        autoFocus
                        onKeyDown={(e) => e.key === "Enter" && handleAddGroup()}
                      />
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => {
                            setShowAddGroup(false);
                            setNewGroupName("");
                          }}
                          className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddGroup}
                          disabled={!newGroupName.trim()}
                          className="px-4 py-2 bg-[#1d57a5] text-white rounded-xl text-sm font-medium hover:bg-[#164280] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add Group
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottom Navigation */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="text-slate-600 dark:text-slate-400 hover:text-[#1d57a5] px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} /> Prev
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setStep(3)}
                      className="bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-500 px-6 py-2.5 rounded-full font-semibold transition-all text-sm"
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleModelNext}
                      disabled={!viewOnly && !modelSaved}
                      className="bg-[#1d57a5] hover:bg-[#164280] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== STEP 3: SQL ANALYTICS ==================== */}
            {step === 3 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                    SQL Analytics Configuration
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    Connect the tenant's primary database to enable generative
                    data analytics.
                  </p>
                </div>
                <div
                  className={`space-y-12 ${viewOnly ? "pointer-events-none opacity-100 select-none" : ""}`}
                >
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Network
                        size={16}
                        className="text-[#1d57a5] dark:text-[#4a8ce3]"
                      />{" "}
                      Connection Credentials
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className={labelCls}>Database Type</label>
                        <select
                          value={testConnectionPayload.databaseType}
                          name="databaseType"
                          className={
                            inputCls + " appearance-none cursor-pointer"
                          }
                          onFocus={handleDropdownOpen}
                          onClick={handleDropdownOpen}
                          onChange={handleChangeTestConnection}
                        >
                          <option value="" disabled>
                            Please Select
                          </option>
                          {dbTypes.map((item) => (
                            <option key={item.value} value={item.text}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>
                          {testConnectionPayload.databaseType.toLowerCase()=="databricks" ? "Host Name" : "Server Name / IP / Host"}
                        </label>
                        <input
                          type="text"
                          name="serverName"
                          value={testConnectionPayload.serverName}
                          onChange={handleChangeTestConnection}
                          className={inputCls}
                        />
                      </div>
                      {testConnectionPayload.databaseType?.toLowerCase() !== "databricks" && (<div>
                        <label className={labelCls}>Database User Name</label>
                        <input
                          type="text"
                          name="sqlDbUserName"
                          id="sqlDbUserName"
                          value={testConnectionPayload.dbUserName}
                          onChange={handleChangeTestConnection}
                          onFocus={(e) => e.target.removeAttribute('readOnly')}
                          readOnly
                          className={inputCls}
                          autoComplete="off"
                          data-auto-complete="off"
                          data-form-type="new-password"
                        />
                      </div>)}
                      <div>
                        <label className={labelCls}>{testConnectionPayload.databaseType.toLowerCase()=="databricks" ? "Token" : "Database Password"}</label>
                        <input
                          type="password"
                          name="sqlDbPassword"
                          id="sqlDbPassword"
                          value={testConnectionPayload.dbPassword}
                          onChange={handleChangeTestConnection}
                          onFocus={(e) => e.target.removeAttribute('readOnly')}
                          readOnly
                          placeholder="••••••••"
                          className={inputCls}
                          autoComplete="off"
                          data-auto-complete="off"
                          data-form-type="new-password"
                        />
                      </div>
                      <div className="md:col-span-2 flex items-end gap-3">
                        <div className="w-1/2">
                          <label className={labelCls}>{testConnectionPayload.databaseType.toLowerCase()=="databricks" ? "Http Path" : "Port Number"}</label>
                          <input
                            type="text"
                            name="portNum"
                            value={testConnectionPayload.portNum}
                            onChange={handleChangeTestConnection}
                            placeholder="e.g. 5432"
                            className={inputCls + " font-mono"}
                          />
                        </div>
                        <button
                          type="button"
                          disabled={connectionSuccess}
                          className="pb-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => testConnection(testConnectionPayload)}
                        >
                          <div className="px-5 py-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:border-[#1d57a5] hover:text-[#1d57a5] dark:hover:border-[#4a8ce3] dark:hover:text-[#4a8ce3] transition-all flex items-center gap-2">
                            <Database size={16} />
                            {connectionSuccess
                              ? "Connected"
                              : "Test Connection"}
                          </div>
                        </button>
                      </div>
                    </div>
                  </section>
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Database size={16} className="text-[#36a852]" /> Target
                      Scope
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label className={labelCls}>Database</label>
                        <select
                          value={selectedDB}
                          onChange={handleDBInfo}
                          className={
                            inputCls +
                            " appearance-none cursor-pointer disabled:opacity-50"
                          }
                        >
                          <option value="" disabled>
                            Please Select
                          </option>
                          {dbData?.map((db) => (
                            <option key={db.value} value={db.text}>
                              {db.text}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{ testConnectionPayload.databaseType.toLowerCase()=="databricks" ? "Catalog" : "Schema"}</label>
                        <select
                          value={selectedSchema}
                          onChange={handleSchemaInfo}
                          className={
                            inputCls +
                            " appearance-none cursor-pointer disabled:opacity-50"
                          }
                        >
                          <option value="" disabled>
                            Please Select
                          </option>
                          {schemaList.map((schema) => (
                            <option key={schema?.value} value={schema?.text}>
                              {schema?.text}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Description</label>
                        <input
                          type="text"
                          name="description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className={inputCls}
                        />
                      </div>
                      <div className="md:col-span-3 grid grid-cols-2 gap-5">
                        <div className="w-full border rounded-lg p-3 max-h-48 overflow-y-auto">
                          <label className="font-semibold mb-2 block">
                            Tables
                          </label>
                          {tableList.map((table) => (
                            <label
                              key={table.objectID}
                              className="flex items-center gap-2 cursor-pointer py-1"
                            >
                              <input
                                type="checkbox"
                                value={table.objectName}
                                checked={selectedTables.includes(
                                  table.objectName,
                                )}
                                onChange={() =>
                                  handleCheckboxChangeForTables(
                                    table.objectName,
                                  )
                                }
                              />
                              {table.objectName}
                            </label>
                          ))}
                        </div>
                        <div className="w-full border rounded-lg p-3 max-h-48 overflow-y-auto">
                          <label className="font-semibold mb-2 block">
                            Views
                          </label>
                          {viewList.map((view) => (
                            <label
                              key={view.objectID}
                              className="flex items-center gap-2 cursor-pointer py-1"
                            >
                              <input
                                type="checkbox"
                                value={view.objectName}
                                checked={selectedViews.includes(
                                  view.objectName,
                                )}
                                onChange={() =>
                                  handleCheckboxChangeForViews(view.objectName)
                                }
                              />
                              {view.objectName}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => {
                      setStep(2);
                    }}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} /> Prev
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setStep(4);
                      }}
                      className="bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-500 px-6 py-2.5 rounded-full font-semibold transition-all text-sm"
                    >
                      Skip
                    </button>
                    <button
                      onClick={
                        viewOnly ? () => setStep(4) : updateSQLConfiguration
                      }
                      className="bg-[#36a852] hover:bg-[#2e9046] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md flex items-center gap-2"
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== STEP 4: FILE ANALYTICS ==================== */}
            {step === 4 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                    File Analytics Configuration
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    Upload enterprise documents to build the custom knowledge
                    base (RAG) for this tenant.
                  </p>
                </div>
                <div
                  className={`space-y-10 ${viewOnly ? "pointer-events-none opacity-100 select-none" : ""}`}
                >
                  <section>
                    <h4 className={sectionHeadCls}>
                      <FileText size={16} className="text-[#36a852]" /> Document
                      Upload
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Left Panel - Manual Upload */}
                      <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-900/30 flex flex-col gap-4">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center">
                            <FileText size={16} className="text-[#3b82f6]" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800 dark:text-white">
                              Manual Upload
                            </p>
                            <p className="text-xs text-slate-500">
                              Upload files from your device
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          id="fileUpload"
                          multiple
                          accept=".pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={viewOnly}
                        />
                        <label
                          htmlFor={viewOnly ? undefined : "fileUpload"}
                          className={`border-2 border-dashed rounded-xl p-4 flex items-center gap-3 transition-colors ${
                            viewOnly
                              ? "border-slate-200 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-800/30 opacity-50 cursor-not-allowed"
                              : "border-[#3b82f6]/30 bg-white dark:bg-slate-800/50 hover:bg-blue-50/50 dark:hover:bg-slate-800 cursor-pointer group"
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-[#3b82f6] text-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform text-lg font-bold">
                            +
                          </div>
                          <div>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 block">
                              Add files
                            </span>
                            <span className="text-xs text-slate-500">
                              PDF, DOCX, TXT · Max 50MB
                            </span>
                          </div>
                        </label>
                        {uploadedFiles.length > 0 && (
                          <div className="space-y-2">
                            {uploadedFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-xl"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileText
                                    size={14}
                                    className="text-[#36a852] flex-shrink-0"
                                  />
                                  <span className="text-xs text-slate-700 dark:text-slate-300 truncate">
                                    {file.name}
                                  </span>
                                  <span className="text-xs text-slate-400 flex-shrink-0">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeFile(index)}
                                  className="ml-1 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors group"
                                >
                                  <X
                                    size={14}
                                    className="text-slate-400 group-hover:text-red-500"
                                  />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Right Panel - Cloud Upload */}
                      <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-2xl p-5 bg-slate-50/50 dark:bg-slate-900/30 flex flex-col gap-4">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-8 h-8 rounded-lg bg-[#1d57a5]/10 flex items-center justify-center">
                            <PlugZap size={16} className="text-[#1d57a5]" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800 dark:text-white">
                              Cloud Upload
                            </p>
                            <p className="text-xs text-slate-500">
                              Connect to cloud storage provider
                            </p>
                          </div>
                        </div>
                        <select
                          value={cloudSource}
                          disabled={viewOnly}
                          onChange={(e) => {
                            setCloudSource(e.target.value);
                            setCloudConfig({
                              bucket: "",
                              region: "",
                              accessKey: "",
                              secretKey: "",
                              containerName: "",
                              connectionString: "",
                              host: "",
                              port: "",
                              username: "",
                              password: "",
                              remotePath: "",
                              folderName: "",
                            });
                            setCloudConnectionStatus("idle");
                            setCloudFiles([]);
                            setSelectedCloudFiles([]);
                          }}
                          className={
                            inputCls + " appearance-none cursor-pointer"
                          }
                        >
                          <option value="">
                            Select cloud provider (optional)
                          </option>
                          <option value="s3">AWS S3</option>
                          <option value="azure">Azure Blob Storage</option>
                          <option value="sftp">SFTP</option>
                        </select>
                        {cloudSource === "s3" && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={labelCls}>
                                Bucket Name{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.bucket}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    bucket: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="my-bucket"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Region <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.region}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    region: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="us-east-1"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Access Key ID{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.accessKey}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    accessKey: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="AKIA..."
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Secret Access Key{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={cloudConfig.secretKey}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    secretKey: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="••••••••"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Folder Name</label>
                              <input
                                type="text"
                                value={cloudConfig.folderName}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    folderName: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="documents/"
                              />
                            </div>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={handleTestCloudConnection}
                                disabled={
                                  !isCloudFormValid() ||
                                  cloudConnectionStatus === "testing" ||
                                  cloudConnectionStatus === "success"
                                }
                                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:border-[#1d57a5] hover:text-[#1d57a5] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-slate-900/50"
                              >
                                {cloudConnectionStatus === "success" ? (
                                  <CheckCircle2
                                    size={15}
                                    className="text-[#36a852]"
                                  />
                                ) : (
                                  <PlugZap size={15} />
                                )}
                                {cloudConnectionStatus === "testing"
                                  ? "Testing…"
                                  : cloudConnectionStatus === "success"
                                    ? "Connected"
                                    : "Test Connection"}
                              </button>
                            </div>
                          </div>
                        )}
                        {cloudSource === "azure" && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={labelCls}>
                                Container Name{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.containerName}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    containerName: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="my-container"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Folder Name</label>
                              <input
                                type="text"
                                value={cloudConfig.folderName}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    folderName: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="documents/"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className={labelCls}>
                                Connection String{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.connectionString}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    connectionString: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="DefaultEndpointsProtocol=https;..."
                              />
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <button
                                type="button"
                                onClick={handleTestCloudConnection}
                                disabled={
                                  !isCloudFormValid() ||
                                  cloudConnectionStatus === "testing" ||
                                  cloudConnectionStatus === "success"
                                }
                                className="px-4 py-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:border-[#1d57a5] hover:text-[#1d57a5] transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-slate-900/50"
                              >
                                {cloudConnectionStatus === "success" ? (
                                  <CheckCircle2
                                    size={15}
                                    className="text-[#36a852]"
                                  />
                                ) : (
                                  <PlugZap size={15} />
                                )}
                                {cloudConnectionStatus === "testing"
                                  ? "Testing…"
                                  : cloudConnectionStatus === "success"
                                    ? "Connected"
                                    : "Test Connection"}
                              </button>
                            </div>
                          </div>
                        )}
                        {cloudSource === "sftp" && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={labelCls}>
                                Host <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.host}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    host: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="sftp.example.com"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Port <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.port}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    port: e.target.value,
                                  }))
                                }
                                className={inputCls + " font-mono"}
                                placeholder="22"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Username <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={cloudConfig.username}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    username: e.target.value,
                                  }))
                                }
                                className={inputCls}
                              />
                            </div>
                            <div>
                              <label className={labelCls}>
                                Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={cloudConfig.password}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    password: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="••••••••"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className={labelCls}>Remote Path</label>
                              <input
                                type="text"
                                value={cloudConfig.remotePath}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    remotePath: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="/uploads/docs"
                              />
                            </div>
                            <div>
                              <label className={labelCls}>Folder Name</label>
                              <input
                                type="text"
                                value={cloudConfig.folderName}
                                onChange={(e) =>
                                  setCloudConfig((p) => ({
                                    ...p,
                                    folderName: e.target.value,
                                  }))
                                }
                                className={inputCls}
                                placeholder="documents/"
                              />
                            </div>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={handleTestCloudConnection}
                                disabled={
                                  !isCloudFormValid() ||
                                  cloudConnectionStatus === "testing" ||
                                  cloudConnectionStatus === "success"
                                }
                                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:border-[#1d57a5] hover:text-[#1d57a5] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-slate-900/50"
                              >
                                {cloudConnectionStatus === "success" ? (
                                  <CheckCircle2
                                    size={15}
                                    className="text-[#36a852]"
                                  />
                                ) : (
                                  <PlugZap size={15} />
                                )}
                                {cloudConnectionStatus === "testing"
                                  ? "Testing…"
                                  : cloudConnectionStatus === "success"
                                    ? "Connected"
                                    : "Test Connection"}
                              </button>
                            </div>
                          </div>
                        )}
                        {cloudFiles.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                Cloud Files ({cloudFiles.length})
                              </p>
                              <button
                                type="button"
                                onClick={toggleAllCloudFiles}
                                className="text-xs text-[#1d57a5] dark:text-[#4a8ce3] hover:underline font-medium"
                              >
                                {selectedCloudFiles.length === cloudFiles.length
                                  ? "Deselect All"
                                  : "Select All"}
                              </button>
                            </div>
                            <div className="border border-slate-200 dark:border-slate-600 rounded-xl overflow-hidden max-h-44 overflow-y-auto">
                              {cloudFiles.map((file, i) => (
                                <label
                                  key={i}
                                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedCloudFiles.includes(file)}
                                    onChange={() => toggleCloudFile(file)}
                                    className="accent-[#36a852] w-3.5 h-3.5 flex-shrink-0"
                                  />
                                  <FileText
                                    size={13}
                                    className="text-[#36a852] flex-shrink-0"
                                  />
                                  <span className="text-xs text-slate-700 dark:text-slate-300 truncate">
                                    {file.fileUrl}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {selectedCloudFiles.length > 0 && (
                              <p className="mt-1.5 text-xs text-slate-500">
                                {selectedCloudFiles.length} of{" "}
                                {cloudFiles.length} file(s) selected
                              </p>
                            )}
                          </div>
                        )}
                        {cloudConnectionStatus === "error" && (
                          <p className="text-xs text-red-500 dark:text-red-400">
                            Failed to connect. Please check your credentials and
                            try again.
                          </p>
                        )}
                      </div>
                    </div>
                    {existingUploadedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                          <FileText size={13} className="text-[#36a852]" />{" "}
                          Existing Uploaded Files
                        </p>
                        {existingUploadedFiles.map((file, index) => (
                          <div
                            key={`${file.fileName}-${file.filePath}-${index}`}
                            className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-xl"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText
                                size={14}
                                className="text-[#36a852] flex-shrink-0"
                              />
                              <span className="text-xs text-slate-700 dark:text-slate-300 truncate">
                                {file.fileName || file.filePath}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setDeleteConfirm({
                                  index,
                                  fileName: file.fileName || file.filePath,
                                })
                              }
                              disabled={viewOnly}
                              className="ml-1 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors group disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Delete file"
                            >
                              <Trash2
                                size={14}
                                className="text-slate-400 group-hover:text-red-500"
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => setStep(3)}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} /> Prev
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setStep(5);
                        setUploadedFiles([]);
                        setSelectedCloudFiles([]);
                      }}
                      className="bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-500 px-6 py-2.5 rounded-full font-semibold transition-all text-sm"
                    >
                      Skip
                    </button>
                    <button
                      onClick={
                        viewOnly
                          ? () => setStep(5)
                          : handleSaveFileConfiguration
                      }
                      className="bg-[#36a852] hover:bg-[#2e9046] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md flex items-center gap-2"
                    >
                      Next <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== STEP 5: AUDIO/VIDEO ANALYTICS ==================== */}
            {step === 5 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                    Audio/Video Analytics Configuration
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    Upload audio and video files to extract insights,
                    transcriptions, and sentiment analysis.
                  </p>
                </div>
                <div
                  className={`space-y-8 ${viewOnly ? "opacity-100 select-none" : ""}`}
                >
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Mic size={16} className="text-purple-500" /> Media Upload
                    </h4>
                    <input
                      type="file"
                      id="mediaUpload"
                      multiple
                      accept="audio/*,video/*"
                      onChange={handleMediaSelect}
                      className="hidden"
                      disabled={viewOnly}
                    />
                    <label
                      htmlFor={viewOnly ? undefined : "mediaUpload"}
                      className={`w-full border-2 border-dashed rounded-2xl p-6 flex items-center gap-4 transition-colors ${
                        viewOnly
                          ? "border-slate-200 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-800/30 opacity-50 cursor-not-allowed"
                          : "border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-900/30 hover:bg-slate-50 dark:hover:bg-slate-800/80 cursor-pointer group"
                      }`}
                    >
                      <div className="w-12 h-12 rounded-full bg-[#3b82f6] text-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform">
                        <Plus size={20} />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 block">
                          Upload Audio/Video Files
                        </span>
                        <span className="text-xs text-slate-500 mt-0.5">
                          Supports MP3, WAV, M4A, MP4, MOV, AVI • Max 1 GB per
                          file
                        </span>
                      </div>
                    </label>
                    {/* Upload Progress Bar with Cancel Button */}
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Uploading...
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-blue-600 dark:text-blue-400">
                              {uploadProgress}%
                            </span>
                            <button
                              onClick={() => {
                                const uploadingIndex = uploadedMedia.findIndex(
                                  (f) => f.uploadStatus === "uploading",
                                );
                                if (uploadingIndex !== -1) {
                                  const fileName =
                                    uploadedMedia[uploadingIndex].name;
                                  setUploadedMedia((prev) =>
                                    prev.filter((_, i) => i !== uploadingIndex),
                                  );
                                  setUploadProgress(0);
                                  showToast(
                                    `Upload cancelled: ${fileName}`,
                                    "error",
                                  );
                                }
                              }}
                              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Cancel upload"
                            >
                              <X size={16} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {isLoadingMedia && (
                      <div className="mt-4 text-center py-4">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                          <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                          Loading existing media...
                        </div>
                      </div>
                    )}
                    {/* Audio Files Table */}
                    {uploadedMedia.filter(
                      (f) =>
                        f.type?.startsWith("audio/") ||
                        f.name?.match(/\.(mp3|wav|m4a|aac|flac)$/i),
                    ).length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Music size={16} className="text-purple-500" />
                          <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Audio Files
                          </h5>
                          <span className="text-xs text-slate-500">
                            (
                            {
                              uploadedMedia.filter(
                                (f) =>
                                  f.type?.startsWith("audio/") ||
                                  f.name?.match(/\.(mp3|wav|m4a|aac|flac)$/i),
                              ).length
                            }{" "}
                            files)
                          </span>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                          <table className="w-full min-w-[800px]">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                              <tr>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[30%]">
                                  File Name
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[15%]">
                                  Size
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[35%]">
                                  Preview
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[15%]">
                                  Status
                                </th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[5%]">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {uploadedMedia.map((file, index) => {
                                const isAudio =
                                  file.type?.startsWith("audio/") ||
                                  file.name?.match(
                                    /\.(mp3|wav|m4a|aac|flac)$/i,
                                  );
                                if (!isAudio) return null;
                                const fileSizeMB = (
                                  file.size /
                                  1024 /
                                  1024
                                ).toFixed(2);
                                const fileUrl = file.file
                                  ? URL.createObjectURL(file.file)
                                  : file.filePath;
                                return (
                                  <tr
                                    key={file.serverId || index}
                                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                                  >
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-2">
                                        <FileAudio
                                          size={16}
                                          className="text-purple-500 flex-shrink-0"
                                        />
                                        <span className="text-sm font-medium text-slate-800 dark:text-white truncate">
                                          {file.name}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                      {fileSizeMB} MB
                                    </td>
                                    <td className="px-4 py-3">
                                      {file.file ? (
                                        <audio
                                          controls
                                          className="h-10 w-full max-w-[200px]"
                                        >
                                          <source
                                            src={fileUrl}
                                            type={file.type}
                                          />
                                        </audio>
                                      ) : (
                                        <span className="text-xs text-slate-400">
                                          Stored on server
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      {file.uploadStatus === "uploading" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                          Uploading {file.uploadProgress}%
                                        </span>
                                      )}
                                      {file.uploadStatus === "completed" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
                                          <CheckCircle2 size={12} />
                                          Completed
                                        </span>
                                      )}
                                      {file.uploadStatus === "error" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 whitespace-nowrap">
                                          <AlertCircle size={12} />
                                          Upload Failed
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <button
                                        onClick={() =>
                                          handleDeleteMediaClick(
                                            index,
                                            file.name,
                                          )
                                        }
                                        disabled={
                                          viewOnly ||
                                          file.uploadStatus === "uploading"
                                        }
                                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-30"
                                      >
                                        <Trash2
                                          size={16}
                                          className={
                                            viewOnly ||
                                            file.uploadStatus === "uploading"
                                              ? "text-slate-300 cursor-not-allowed"
                                              : "text-slate-400 hover:text-red-500"
                                          }
                                        />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {/* Video Files Table */}
                    {uploadedMedia.filter(
                      (f) =>
                        f.type?.startsWith("video/") ||
                        f.name?.match(/\.(mp4|mov|avi|mkv|webm)$/i),
                    ).length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Video size={16} className="text-blue-500" />
                          <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Video Files
                          </h5>
                          <span className="text-xs text-slate-500">
                            (
                            {
                              uploadedMedia.filter(
                                (f) =>
                                  f.type?.startsWith("video/") ||
                                  f.name?.match(/\.(mp4|mov|avi|mkv|webm)$/i),
                              ).length
                            }{" "}
                            files)
                          </span>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                          <table className="w-full min-w-[800px]">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                              <tr>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[25%]">
                                  File Name
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[10%]">
                                  Size
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[45%]">
                                  Preview
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[15%]">
                                  Status
                                </th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider w-[5%]">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {uploadedMedia.map((file, index) => {
                                const isVideo =
                                  file.type?.startsWith("video/") ||
                                  file.name?.match(
                                    /\.(mp4|mov|avi|mkv|webm)$/i,
                                  );
                                if (!isVideo) return null;
                                const fileSizeMB = (
                                  file.size /
                                  1024 /
                                  1024
                                ).toFixed(2);
                                const fileUrl = file.file
                                  ? URL.createObjectURL(file.file)
                                  : file.filePath;
                                return (
                                  <tr
                                    key={file.serverId || index}
                                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                                  >
                                    <td className="px-4 py-3 align-top">
                                      <div className="flex items-center gap-2">
                                        <FileVideo
                                          size={16}
                                          className="text-blue-500 flex-shrink-0"
                                        />
                                        <span
                                          className="text-sm font-medium text-slate-800 dark:text-white truncate max-w-[180px]"
                                          title={file.name}
                                        >
                                          {file.name}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap align-top">
                                      {fileSizeMB} MB
                                    </td>
                                    <td className="px-4 py-3">
                                      {file.file ? (
                                        <div className="relative w-full max-w-[280px]">
                                          <video
                                            controls
                                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm bg-black/5"
                                            style={{ maxHeight: "120px" }}
                                            preload="metadata"
                                          >
                                            <source
                                              src={fileUrl}
                                              type={file.type}
                                            />
                                            Your browser does not support the
                                            video tag.
                                          </video>
                                        </div>
                                      ) : (
                                        <span className="text-xs text-slate-400">
                                          Stored on server
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                      {file.uploadStatus === "uploading" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                          Uploading {file.uploadProgress}%
                                        </span>
                                      )}
                                      {file.uploadStatus === "completed" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
                                          <CheckCircle2 size={12} />
                                          Completed
                                        </span>
                                      )}
                                      {file.uploadStatus === "error" && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 whitespace-nowrap">
                                          <AlertCircle size={12} />
                                          Upload Failed
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                      <button
                                        onClick={() =>
                                          handleDeleteMediaClick(
                                            index,
                                            file.name,
                                          )
                                        }
                                        disabled={
                                          viewOnly ||
                                          file.uploadStatus === "uploading"
                                        }
                                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-30"
                                      >
                                        <Trash2
                                          size={16}
                                          className={
                                            viewOnly ||
                                            file.uploadStatus === "uploading"
                                              ? "text-slate-300 cursor-not-allowed"
                                              : "text-slate-400 hover:text-red-500"
                                          }
                                        />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {!isLoadingMedia && uploadedMedia.length === 0 && (
                      <div className="mt-6 text-center py-8 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-700">
                        <FileAudio
                          size={32}
                          className="text-slate-300 dark:text-slate-600 mx-auto mb-2"
                        />
                        <p className="text-sm text-slate-500">
                          No audio or video files uploaded yet
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Click the upload button above to add files
                        </p>
                      </div>
                    )}
                  </section>
                  {/* Processing Options block removed */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <button
                      onClick={() => setStep(4)}
                      className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                    >
                      <ArrowLeft size={16} /> Prev
                    </button>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setStep(6)}
                        className="bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-500 px-6 py-2.5 rounded-full font-semibold transition-all text-sm"
                      >
                        Skip
                      </button>
                      <button
                        onClick={
                          viewOnly ? () => setStep(6) : saveMediaConfiguration
                        }
                        disabled={
                          !viewOnly &&
                          uploadedMedia.some(
                            (f) =>
                              !f.uploadStatus || f.uploadStatus !== "completed",
                          )
                        }
                        className={`bg-[#36a852] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md flex items-center gap-2 ${!viewOnly && uploadedMedia.some((f) => !f.uploadStatus || f.uploadStatus !== "completed") ? "opacity-50 cursor-not-allowed" : "hover:bg-[#2e9046]"}`}
                      >
                        Next <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== STEP 6: ML SETUP ==================== */}
            {step === 6 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-24">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                  ML Setup
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-10">
                  Configure ML sources and prediction models for this tenant.
                </p>

                <div
                  className={`space-y-12 ${viewOnly ? "pointer-events-none opacity-100 select-none" : ""}`}
                >
                  <section>
                    <h4 className={sectionHeadCls}>
                      <Database
                        size={16}
                        className="text-[#1d57a5] dark:text-[#4a8ce3]"
                      />{" "}
                      ML Dataset &amp; Model Setup
                    </h4>
                    <label
                      htmlFor={viewOnly ? undefined : "mlDatasetUpload"}
                      className={`border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl p-4 text-center block ${viewOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:border-[#1d57a5] transition-colors"}`}
                    >
                      <Cpu size={22} className="mx-auto mb-2 text-slate-400" />
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-medium">
                        Drag and drop your CSV or Excel file here
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Supports .csv, .xlsx, .xls
                      </p>
                      <span className="mt-3 inline-flex items-center px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {isUploadingMlDataset ? "Uploading..." : "Browse Files"}
                      </span>
                    </label>
                    {!viewOnly && (
                      <input
                        id="mlDatasetUpload"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleMlDatasetUpload}
                        disabled={isUploadingMlDataset}
                      />
                    )}

                    <div className="mt-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                      <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                        Uploaded Datasets
                      </p>
                      {mlDataSets.length === 0 ? (
                        <p className="text-xs text-slate-500">No dataset visible yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {mlDataSets.map((item, idx) => {
                            const fileName =
                              pickVal(item, ["originalFileName", "fileName", "sourceName", "name"]) ||
                              pickVal(item, ["filePath", "path"]) ||
                              `Dataset ${idx + 1}`;
                            return (
                              <span
                                key={`dataset_badge_${idx}_${fileName}`}
                                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                              >
                                {String(fileName)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 sm:p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          Existing Created Models
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300">
                          {mlExistingModels.length} total
                        </span>
                      </div>
                      {mlExistingModels.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                          No models created yet.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {mlExistingModels.map((model, idx) => {
                            const modelId = getMlModelId(model);
                            const modelName =
                              pickVal(model, ["mlModelName", "modelName", "name"]) ||
                              `Model ${idx + 1}`;
                            const modelType =
                              pickVal(model, ["preferredModel", "mlModelType", "modelType", "algorithm"]) ||
                              "N/A";
                            const statusRaw = pickVal(model, ["mlModelStatus", "status"]);
                            const status = normalizeMlStatus(statusRaw);
                            const isSuccessStatus =
                              status === "succeded" || status === "succeeded";
                            const statusLabel =
                              status === "running"
                                ? "Running"
                                : isSuccessStatus
                                  ? "Succeeded"
                                  : status === "failed"
                                    ? "Failed"
                                    : "Not Built";
                            const statusClass =
                              status === "running"
                                ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30"
                                : isSuccessStatus
                                  ? "bg-green-100 text-green-700 border-green-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30"
                                  : status === "failed"
                                    ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30"
                                    : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600";
                            const mapeValue = Number(
                              pickVal(model, ["mlModelMape", "modelMape"]) || NaN,
                            );
                            const hasMape = Number.isFinite(mapeValue);
                            const isBuilding = buildingMlModelIds.includes(modelId);
                            const modelSourceId = String(
                              pickVal(model, ["sourceId", "datasetId"]) || "",
                            );
                            const sourceDataset = mlDataSets.find(
                              (item) =>
                                String(pickVal(item, ["sourceId", "id", "datasetId"])) ===
                                modelSourceId,
                            );
                            const sourceLabel = sourceDataset
                              ? getDatasetDisplayName(sourceDataset)
                              : modelSourceId
                                ? `Source #${modelSourceId}`
                                : "Unknown Source";
                            return (
                              <div
                                key={`existing_model_${modelId || idx}`}
                                className="group flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3.5 bg-slate-50/60 dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                              >
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                                      {String(modelName)}
                                    </p>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                      {String(modelType)}
                                    </span>
                                    {hasMape ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                        MAPE: {mapeValue.toFixed(2)}
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                                    Source: {sourceLabel}
                                  </p>
                                  <p className="mt-2">
                                    <span
                                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${statusClass}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
                                      {statusLabel}
                                    </span>
                                  </p>
                                </div>
                                {!viewOnly && (
                                  <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                                    <button
                                      type="button"
                                      onClick={() => handleBuildExistingMlModel(model)}
                                      disabled={
                                        isBuilding || status === "running" || !modelId
                                      }
                                      className="inline-flex items-center justify-center gap-1.5 min-w-[96px] px-4 py-2 rounded-full text-xs font-semibold border border-[#1d57a5] text-[#1d57a5] hover:bg-[#1d57a5]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Play size={12} />{" "}
                                      {isBuilding || status === "running"
                                        ? "Rebuilding..."
                                        : "Rebuild"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleEditExistingMlModel(model)}
                                      className="inline-flex items-center justify-center gap-1.5 min-w-[84px] px-4 py-2 rounded-full text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                                    >
                                      <Edit2 size={12} /> Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteExistingMlModel(model)}
                                      disabled={isDeletingMlModel}
                                      className="inline-flex items-center justify-center gap-1.5 min-w-[88px] px-4 py-2 rounded-full text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Trash2 size={12} /> Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h4 className={sectionHeadCls}>
                      <Cpu size={16} className="text-[#36a852]" /> Prediction Models
                    </h4>
                    {mlModelCards.map((card, index) => (
                      <div
                        key={card.localId}
                        className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm"
                      >
                        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                          <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                            Add New Prediction Model {mlModelCards.length > 1 ? `#${index + 1}` : ""}
                          </h3>
                          {!viewOnly && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleSaveMlModels}
                                disabled={isSavingMlModels || isUploadingMlDataset}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-[#1d57a5] hover:bg-[#164280] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {isSavingMlModels
                                  ? "Saving..."
                                  : editingMlModelId
                                    ? "Update"
                                    : "Save"}
                              </button>
                              <button
                                onClick={() => removeMlModelCard(card.localId)}
                                className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelCls}>
                              Source Dataset <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={card.sourceId || ""}
                              onChange={(e) =>
                                handleMlCardSourceChange(card.localId, e.target.value)
                              }
                              className={inputCls}
                              disabled={viewOnly || mlDataSets.length === 0}
                            >
                              <option value="">Select dataset source</option>
                              {mlDataSets.map((item, sourceIdx) => {
                                const sourceId = String(
                                  pickVal(item, ["sourceId", "id", "datasetId"]) || "",
                                );
                                const sourceLabel = getDatasetDisplayName(
                                  item,
                                  `Dataset ${sourceIdx + 1}`,
                                );
                                return (
                                  <option key={`card_source_${sourceId}_${sourceIdx}`} value={sourceId}>
                                    {sourceLabel}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                          {card.sourceId ? (
                            <>
                              <div>
                                <label className={labelCls}>
                                  Model Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                  value={card.modelName}
                                  onChange={(e) =>
                                    setMlModelCardField(card.localId, "modelName", e.target.value)
                                  }
                                  placeholder="e.g. Q3 Revenue Forecast"
                                  className={inputCls}
                                  disabled={viewOnly}
                                />
                              </div>
                              <div>
                                <label className={labelCls}>
                                  Algorithm / Model Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={card.preferredModel}
                                  onChange={(e) =>
                                    setMlModelCardField(
                                      card.localId,
                                      "preferredModel",
                                      e.target.value,
                                    )
                                  }
                                  className={inputCls}
                                  disabled={viewOnly}
                                >
                                  <option value="">Select a model algorithm</option>
                                  {mlModelTypes.map((item, typeIndex) => (
                                    <option key={`${item.value}_${typeIndex}`} value={item.value}>
                                      {item.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className={labelCls}>Details / Description</label>
                                <input
                                  value={card.description}
                                  onChange={(e) =>
                                    setMlModelCardField(card.localId, "description", e.target.value)
                                  }
                                  placeholder="Brief description of this model"
                                  className={inputCls}
                                  disabled={viewOnly}
                                />
                              </div>
                              <div>
                                <label className={labelCls}>Time Column (X-Axis)</label>
                                <select
                                  value={card.timeColumn}
                                  onChange={(e) =>
                                    setMlModelCardField(card.localId, "timeColumn", e.target.value)
                                  }
                                  className={inputCls}
                                  disabled={viewOnly || mlColumns.length === 0}
                                >
                                  <option value="">Select time index</option>
                                  {mlColumns.map((column) => (
                                    <option key={`time_${column}`} value={column}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className={labelCls}>
                                  Target Variable (Y-Axis) <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={card.targetColumn}
                                  onChange={(e) =>
                                    setMlModelCardField(card.localId, "targetColumn", e.target.value)
                                  }
                                  className={inputCls}
                                  disabled={viewOnly || mlColumns.length === 0}
                                >
                                  <option value="">Select target variable</option>
                                  {mlColumns.map((column) => (
                                    <option key={`target_${column}`} value={column}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className={labelCls}>
                                  Select Features (Independent Variables){" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 min-h-[110px] bg-slate-50 dark:bg-slate-900/30">
                                  {mlColumns.length === 0 ? (
                                    <p className="text-sm text-slate-500">
                                      Upload a CSV file to auto-populate columns.
                                    </p>
                                  ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      {mlColumns.map((column) => (
                                        <label
                                          key={`feature_${card.localId}_${column}`}
                                          className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={card.featureColumns.includes(column)}
                                            onChange={() =>
                                              toggleFeatureColumn(card.localId, column)
                                            }
                                            disabled={viewOnly}
                                          />
                                          <span className="truncate">{column}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="md:col-span-2 text-sm text-slate-500 dark:text-slate-400 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-3">
                              Select a source dataset to load available columns and continue.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {!viewOnly && (
                      <div className="flex items-center gap-2">
                        {editingMlModelId ? (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMlModelId(null);
                              setMlModelCards([]);
                            }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm"
                          >
                            Cancel Edit
                          </button>
                        ) : null}
                      </div>
                    )}
                    {!viewOnly && (
                      <button
                        onClick={addMlModelCard}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#1d57a5] text-[#1d57a5] hover:bg-[#1d57a5]/5 font-semibold text-sm"
                      >
                        <Plus size={16} /> Add New Model.....
                      </button>
                    )}
                  </section>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => setStep(5)}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} /> Prev
                  </button>
                  <div className="flex items-center gap-3">
                    {!viewOnly && (
                      <button
                        onClick={() => setStep(7)}
                        className="bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-500 px-6 py-2.5 rounded-full font-semibold transition-all text-sm"
                      >
                        Skip
                      </button>
                    )}
                    {!viewOnly && (
                      <button
                        onClick={() => setStep(7)}
                        className="bg-[#36a852] hover:bg-[#2e9046] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md flex items-center gap-2"
                      >
                        Next <ChevronRight size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}



            {/* ==================== STEP 7: REVIEW & FINISH ==================== */}
            {step === 7 && (
              <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 pb-24">
                <div className="mb-10 text-center">
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                    Review &amp; Finish
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400 text-sm max-w-lg mx-auto">
                    Please review the tenant configuration before initializing
                    the data ingestion pipelines.
                  </p>
                </div>
                <div className="bg-slate-50/50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 rounded-[2rem] overflow-hidden shadow-sm">
                  {[
                    {
                      icon: Video,
                      bg: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
                      title: "Audio/Video Analytics",
                      sub: "Media transcription & analysis",
                      right: (
                        <div className="flex items-center gap-8">
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Media Files
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/30 font-bold text-purple-700 dark:text-purple-400 text-xs border border-purple-200 dark:border-purple-800/50">
                              {uploadedMedia.length || 0} Uploaded
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Total Size
                            </span>
                            <span className="font-medium text-sm text-slate-800 dark:text-slate-200">
                              {(
                                uploadedMedia.reduce((acc, file) => {
                                  if (!file || !file.size) return acc;
                                  return acc + file.size;
                                }, 0) /
                                1024 /
                                1024
                              ).toFixed(2)}{" "}
                              MB
                            </span>
                          </div>
                          {!viewOnly ? (
                            <div>
                              <button
                                onClick={handleMediaSeeding}
                                disabled={uploadedMedia.length == 0}
                                className={`ad-btn ad-btn-blue text-white px-4 py-2 rounded-full font-medium transition-all shadow-md flex items-center gap-2 ${
                                  uploadedMedia.length == 0
                                    ? "opacity-50 cursor-not-allowed backdrop-blur-sm pointer-events-none"
                                    : "hover:bg-[#2e9046]"
                                }`}
                              >
                                {isStartingMediaIngestion ? (
                                  <>
                                    In process... <Loader size={16} />
                                  </>
                                ) : (
                                  "Ingestion"
                                )}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ),
                    },
                    {
                      icon: Database,
                      bg: "bg-amber-500/10 text-amber-600 dark:text-amber-500",
                      title: "SQL Analytics",
                      sub: "Database connection scoping",
                      right: (
                        <div className="flex items-center gap-8">
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Target Tables
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 font-bold text-amber-700 dark:text-amber-500 text-xs border border-amber-200 dark:border-amber-800/50">
                              {selectedTablesArray.length || 0} Selected
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Database Type
                            </span>
                            <span className="font-medium text-sm text-slate-800 dark:text-slate-200">
                              {testConnectionPayload.databaseType || "Not Selected"}
                            </span>
                          </div>
                          {!viewOnly ? (
                            <div>
                              <button
                                onClick={handleDBSeeding}
                                disabled={!selectedTablesArray?.length}
                                className={`ad-btn ad-btn-blue text-white px-4 py-2 rounded-full font-medium transition-all shadow-md flex items-center gap-2 ${
                                  !selectedTablesArray?.length
                                    ? "opacity-50 cursor-not-allowed backdrop-blur-sm pointer-events-none"
                                    : "hover:bg-[#2e9046]"
                                }`}
                              >
                                {isStartingDBIngestion ? (
                                  <>
                                    In process... <Loader size={16} />
                                  </>
                                ) : (
                                  "Ingestion"
                                )}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ),
                    },
                    {
                      icon: FileText,
                      bg: "bg-[#36a852]/10 text-[#36a852]",
                      title: "File Analytics",
                      sub: "Document indexing & RAG",
                      right: (
                        <div className="flex items-center gap-8">
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Documents
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/30 font-bold text-green-700 dark:text-green-400 text-xs border border-green-200 dark:border-green-800/50">
                              {([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length || 0)}{" "}
                              Uploaded
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs text-slate-500 mb-1">
                              Total Size
                            </span>
                            <span className="font-medium text-sm text-slate-800 dark:text-slate-200">
                              {(
                                [...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].reduce((acc, file) => {
                                  if (!file || !file.size) return acc;
                                  if (typeof file.size === "number") return acc + file.size;
                                  if (typeof file.size === "string") {
                                    const [value, unit] = file.size.trim().split(" ");
                                    const size = parseFloat(value);
                                    const unitMap = {
                                      KB: 1024,
                                      MB: 1024 * 1024,
                                      GB: 1024 * 1024 * 1024,
                                    };
                                    return acc + size * (unitMap[unit?.toUpperCase()] || 1);
                                  }
                                  return acc;
                                }, 0) /
                                1024 /
                                1024
                              ).toFixed(2) || 0}{" "}
                              MB
                            </span>
                          </div>
                          {!viewOnly ? (
                            <div>
                              <button
                                onClick={handleFileSeeding}
                                disabled={
                                  ([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length || 0) == 0
                                }
                                className={`ad-btn ad-btn-blue text-white px-4 py-2 rounded-full font-medium transition-all shadow-md flex items-center gap-2 ${
                                  ([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length || 0) === 0
                                    ? "opacity-50 cursor-not-allowed backdrop-blur-sm pointer-events-none"
                                    : "hover:bg-[#2e9046]"
                                }`}
                              >
                                {isStartingFileIngestion ? (
                                  <>
                                    In process... <Loader size={16} />
                                  </>
                                ) : (
                                  "Ingestion"
                                )}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ),
                    },
                  ].map((row, i, arr) => {
                    const Icon = row.icon;
                    return (
                      <div
                        key={i}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 hover:bg-white dark:hover:bg-slate-800/50 transition-colors ${i < arr.length - 1 ? "border-b border-slate-200 dark:border-slate-700" : ""}`}
                      >
                        <div className="flex items-center gap-4 mb-4 sm:mb-0">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${row.bg}`}
                          >
                            <Icon size={20} />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-slate-800 dark:text-white">
                              {row.title}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {row.sub}
                            </p>
                          </div>
                        </div>
                        <div className="pl-14 sm:pl-0 text-left sm:text-right">
                          {row.right}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!viewOnly ? (
                  <div className="flex justify-end mt-4 mr-4">
                    <button
                      onClick={handleFinishAndStartIngestion}
                      disabled={
                        uploadedMedia.length == 0 &&
                        selectedTablesArray.length == 0 &&
                        ([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length || 0) == 0
                      }
                      className={`ad-btn ad-btn-blue text-white px-8 py-3 rounded-full font-medium transition-all shadow-md flex items-center gap-2 ${
                        uploadedMedia.length == 0 &&
                        selectedTablesArray.length == 0 &&
                        ([...existingUploadedFiles, ...uploadedFiles, ...selectedCloudFiles].length || 0) == 0
                          ? "opacity-50 cursor-not-allowed backdrop-blur-sm pointer-events-none"
                          : "hover:bg-[#2e9046]"
                      }`}
                    >
                      {isStartingMediaIngestion ? (
                        <>
                          In process... <Loader size={16} />
                        </>
                      ) : (
                        "Ingestion All"
                      )}
                    </button>
                  </div>
                ) : null}

                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => setStep(5)}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} /> Edit Configuration
                  </button>
                  <div className="flex flex-row items-end gap-2">
                    {seedStartError && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {seedStartError}
                      </p>
                    )}

                    {!viewOnly && (
                      <button
                        onClick={() => setStep(7)}
                        disabled={
                          isStartingFileIngestion ||
                          isStartingDBIngestion ||
                          isStartingMediaIngestion ||
                          isStartingIngestion
                        }
                        className="bg-[#36a852] hover:bg-[#2e9046] text-white px-8 py-3 rounded-full font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md"
                      >
                        Next <ChevronRight size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </div>

      {/* ==================== DELETE CONFIRMATION MODALS ==================== */}
      {deleteConfirm && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => {
            removeExistingFile(deleteConfirm.index);
            setDeleteConfirm(null);
          }}
          useCaseName={deleteConfirm.fileName}
        />
      )}
      {deleteMediaConfirm && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setDeleteMediaConfirm(null)}
          onConfirm={confirmDeleteMedia}
          useCaseName={deleteMediaConfirm.fileName}
        />
      )}

      {/* ==================== TOAST NOTIFICATION ==================== */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl text-sm font-medium transition-all animate-in fade-in slide-in-from-top-4 duration-300 ${toast.type === "error" ? "bg-red-500 text-white" : "bg-[#36a852] text-white"}`}
        >
          {toast.type === "error" ? (
            <AlertCircle size={16} className="flex-shrink-0" />
          ) : (
            <CheckCircle2 size={16} className="flex-shrink-0" />
          )}
          {toast.message}
          <button
            onClick={() => setToast(null)}
            className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <style>{`@keyframes progress { from { width: 0%; } to { width: 100%; } }`}</style>
    </>
  );
}
